import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedService } from '@services/shared.service';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SharedModule } from '@shared/shared.module';
import { PagesService } from '@services/pages.service';
import { QRCodeComponent } from 'angularx-qrcode';
import { environment } from '@env';
import { Clipboard } from '@angular/cdk/clipboard';
import { v4 as uuidv4 } from 'uuid';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { AuthService } from 'src/app/Auth/AuthService';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FilterConfig, FilterModalComponent } from '@pages/filter-modal/filter-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { isArabicLang, ensureArabicFont, reshapeArabic, containsArabic, arabicAwareCellHook, drawBilingualText, renderArabicTextAsImage } from 'src/app/utils/arabic-pdf.util';

interface ContestPerformance {
  totalViews: number | null;
  completedTasks: number | null;
  completedTasksIsRate?: boolean;
  avgClickRate: number | null;
  avgCompletionRate: number | null;
  participationRate: number | null;
  totalViewsTrend?: number | null;
  completedTasksTrend?: number | null;
  avgClickRateTrend?: number | null;
  participationRateTrend?: number | null;
  totalViewsTrendLabel?: string | null;
  totalViewsTrendClass?: string | null;
  completedTasksTrendLabel?: string | null;
  completedTasksTrendClass?: string | null;
  avgClickRateTrendLabel?: string | null;
  avgClickRateTrendClass?: string | null;
  participationRateTrendLabel?: string | null;
  participationRateTrendClass?: string | null;
}

interface ContestPerson {
  id: number | string;
  name: string;
  email: string;
}

interface OverviewStats {
  totalContests: number;
  activeContests: number;
  pendingContests: number;
  finishedContests: number;
  avgClickRate: number;
  avgCompletionRate: number;
  avgParticipationRate: number;
  avgEngagementRate: number;
}

@Component({
  selector: 'app-constant-detail',
  standalone: true,
  imports: [RouterLink, CommonModule, SharedModule, QRCodeComponent, ReactiveFormsModule, FormsModule, NzModalModule, NzSelectModule, NzTableModule, NzToolTipModule, NzDrawerModule, NzDatePickerModule, FilterModalComponent],
  templateUrl: './constant-detail.component.html',
  styleUrls: ['./constant-detail.scss']
})
export class ConstantDetailComponent implements OnInit, OnDestroy {

  contest_detail: any[] = [];
  baseUrl = environment.apiUrl;
  http = inject(HttpClient);
  sharedService = inject(SharedService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  pagesService = inject(PagesService);
  clipboard = inject(Clipboard);
  contests_detail: any = {};
  name!: string;
  arabic_name: string = '';
  created_at!: string;
  description!: string;
  arabic_description: string = '';
  isDescriptionExpanded: boolean = false;

  get isDescriptionLong(): boolean {
    const desc = (this.isArabic && this.arabic_description) ? this.arabic_description : this.description;
    if (!desc) return false;
    const raw = desc.replace(/<[^>]*>/g, '').trim();
    return raw.length > 100;
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  // Submission metadata shown on the Contest Details page.
  submitted_by: ContestPerson | null = null;
  submitted_via: string | null = null;
  submitted_on!: string;
  approved_by: ContestPerson | null = null;
  levels_of_contest: any[] = [];
  _contest_prize: any[] = [];
  banners: any[] = [];
  start_date!: string;
  end_date!: string;
  draw_date!: string;
  contestID: any;
  showAllVariables: { [key: string]: boolean } = {};
  maxVariablesToShow: number = 3;
  editingLevels: { [key: string]: boolean } = {};
  levelForms: { [key: string]: FormGroup } = {};
  levelImagePreviews: { [key: string]: string | ArrayBuffer | null } = {};
  levelImageLoadErrors: { [key: string]: boolean } = {};
  isImageModalVisible: { [key: string]: boolean } = {};
  fb = inject(FormBuilder);
  authService = inject(AuthService);
  translateService = inject(TranslateService);
  cdr = inject(ChangeDetectorRef);

  onLevelImageError(levelId: string): void {
    this.levelImageLoadErrors[levelId] = true;
    this.cdr.detectChanges();
  }

  get isArabic(): boolean {
    return localStorage.getItem('lang') === 'ar';
  }

  // Contest Performance and Participants data
  contestPerformance: ContestPerformance = {
    totalViews: null,
    completedTasks: null,
    avgClickRate: null,
    avgCompletionRate: null,
    participationRate: null,
    totalViewsTrend: null,
    completedTasksTrend: null,
    avgClickRateTrend: null,
    participationRateTrend: null,
    totalViewsTrendLabel: null,
    totalViewsTrendClass: null,
    completedTasksTrendLabel: null,
    completedTasksTrendClass: null,
    avgClickRateTrendLabel: null,
    avgClickRateTrendClass: null,
    participationRateTrendLabel: null,
    participationRateTrendClass: null
  };
  performanceStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performancePreviousStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performanceTrends: Record<keyof OverviewStats | 'overallSuccessRate', number> = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0,
    overallSuccessRate: 0
  };
  performanceLoading: boolean = false;
  performanceError: string | null = null;
  performanceDateRange: Date[] = [];
  performancePickerOpen: boolean = false;
  participantsList: any[] = [];
  filteredParticipants: any[] = [];
  participantsLoading: boolean = false;
  winnersList: any[] = [];
  winnersLoading: boolean = false;
  analyticsActivity: any[] = [];
  analyticsLoading: boolean = false;
  analyticsLoaded: boolean = false;
  quizzesList: any[] = [];
  quizzesLoaded: boolean = false;

  // Level Matrix — per-level completion + demographic breakdown
  levelMatrix: any = null;
  levelMetricsLoading: boolean = false;
  levelMetricsError: string = '';
  levelDemographicsDrawerVisible: boolean = false;
  selectedLevelMetrics: any = null;

  // Search and Filter for participants
  searchTerm: string = '';
  filterModalVisible: boolean = false;
  currentFilters: any = {};
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;

  filterConfig: FilterConfig = {
    showTimeline: false,
    showLevel: false,
    showStatus: true,
    showFormat: false,
    showType: false,
    showViewType: false,
    showLocation: true,
    // Web QR collects no gender, so the filter is gone along with the column.
    showGender: false,
    showContest: false,
    showAge: false,
    showCompletionRate: false,
    showParticipantName: false,
    showParticipantEmail: false,
    statusOptions: [
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' }
    ]
  };

  // Drawer for participant levels
  levelsDrawerVisible: boolean = false;
  selectedParticipant: any = null;
  selectedParticipantLevels: any[] = [];

  // Leaderboard
  leaderboard: any[] = [];
  leaderboardLoading: boolean = false;
  leaderboardPage: number = 1;
  leaderboardPerPage: number = 10;
  leaderboardTotal: number = 0;

  // Admin override
  overrideModalVisible: boolean = false;
  overrideParticipantId: any = null;
  overrideReason: string = '';
  overrideSubmitting: boolean = false;
  overrideAuditLog: any[] = [];

  private pdfSectionHeading(doc: jsPDF, text: string, y: number, arabicFontLoaded: boolean = false): number {
    const pageWidth = doc.internal.pageSize.width;
    const isAr = containsArabic(text);
    const isArabicApp = isArabicLang();
    let bottomY = y + 5;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(60, 52, 137);

    if (isAr && isArabicApp) {
      drawBilingualText(doc, text, pageWidth - 14, y + 4, arabicFontLoaded, { align: 'right' });
      bottomY = y + 5;
    } else {
      drawBilingualText(doc, text, 14, y + 4, arabicFontLoaded);
      bottomY = y + 5;
    }

    const lineY = bottomY + 2;
    doc.setDrawColor(60, 52, 137);
    doc.setLineWidth(0.4);
    doc.line(14, lineY, pageWidth - 14, lineY);
    return lineY + 6;
  }

  private pdfEnsureSpace(doc: jsPDF, y: number, needed: number): number {
    const pageHeight = doc.internal.pageSize.height;
    if (y + needed > pageHeight - 15) {
      doc.addPage();
      return 15;
    }
    return y;
  }

  get hasSurveyLevel(): boolean {
    return (this.levels_of_contest || []).some(
      (level: any) => (level.level_type || level.type) === 'survey'
    );
  }

  isMcqQuestion(q: any): boolean {
    if (!q) return false;
    if (q.question_type === 'mcq' || q.type === 'mcq') return true;
    if (q.question_type === 'open_text' && (!q.options || q.options.length === 0)) return false;
    return Array.isArray(q.options) && q.options.length > 0;
  }

  // Exports every participant's submitted survey answers to Excel. Resolves answers
  // from users_progress, participant levels, survey_answers, or level user_answer,
  // mapping question texts from survey_questions definitions when plain answers are provided.
  exportSurveyResponses(): void {
    const surveyLevels = (this.levels_of_contest || []).filter(
      (level: any) => (level.level_type || level.type) === 'survey'
    );

    if (surveyLevels.length === 0) {
      this.sharedService.createNotification('warning', 'This engagement has no survey level.');
      return;
    }

    const rows: any[] = [];

    (this.participantsList || []).forEach((participant: any) => {
      const userId = participant?._end_user?.id ?? participant?.participant_id ?? participant?.id;
      const participantName = this.getParticipantName(participant);
      const participantEmail = participant?._end_user?.email || participant?.email || '-';
      const participantCountry = participant?._end_user?.country || participant?.country || participant?.location || '-';
      const participantLevels: any[] = participant?.levelsDetail || participant?.levels || participant?.contest?.levels || participant?.contest?._levels_of_contest || [];

      surveyLevels.forEach((surveyLevel: any) => {
        const surveyLevelName = surveyLevel.name || surveyLevel.textContent || surveyLevel.level_name || `Level ${surveyLevel.id}`;
        const surveyQuestions: any[] = surveyLevel.survey_questions || [];

        // Try to find the participant's level progress across all possible structures
        const matchedParticipantLevel = participantLevels.find((l: any) =>
          l.id === surveyLevel.id || (l.level_type || l.type) === 'survey' || l.name === surveyLevel.name
        );

        const progress = (surveyLevel.users_progress || []).find((up: any) => up.user_id === userId || up.participant_id === userId)
          || (matchedParticipantLevel?.users_progress || []).find((up: any) => up.user_id === userId || up.participant_id === userId);

        const rawUserAnswer = progress?.user_answer ?? progress?.submitted_answer ?? progress?.answer ?? progress?.response
          ?? matchedParticipantLevel?.user_answer ?? matchedParticipantLevel?.submitted_answer ?? matchedParticipantLevel?.answer
          ?? participant?.survey_answers ?? participant?.answers ?? null;

        const levelStatus = progress?.status ?? matchedParticipantLevel?.status ?? participant?.contestStatus ?? participant?.status ?? 'completed';

        if (!rawUserAnswer && !progress && !matchedParticipantLevel) {
          return;
        }

        const parsedAnswers = this.parseSurveyAnswer(rawUserAnswer, surveyQuestions);

        if (parsedAnswers.length === 0) {
          if (surveyQuestions.length > 0) {
            surveyQuestions.forEach((sq: any, sIdx: number) => {
              const qText = this.isArabic ? (sq.arabic_question_text || sq.question_text) : (sq.question_text || `Question ${sIdx + 1}`);
              rows.push({
                'Participant Name': participantName,
                'Email': participantEmail,
                'Country': participantCountry,
                'Survey Level': surveyLevelName,
                'Question': qText,
                'Answer': rawUserAnswer ? String(rawUserAnswer) : '-',
                'Status': levelStatus,
              });
            });
          } else {
            rows.push({
              'Participant Name': participantName,
              'Email': participantEmail,
              'Country': participantCountry,
              'Survey Level': surveyLevelName,
              'Question': 'Survey Response',
              'Answer': rawUserAnswer ? String(rawUserAnswer) : '-',
              'Status': levelStatus,
            });
          }
        } else {
          parsedAnswers.forEach((qa) => {
            rows.push({
              'Participant Name': participantName,
              'Email': participantEmail,
              'Country': participantCountry,
              'Survey Level': surveyLevelName,
              'Question': qa.question,
              'Answer': qa.answer,
              'Status': levelStatus,
            });
          });
        }
      });
    });

    if (rows.length === 0) {
      this.sharedService.createNotification('warning', 'No survey responses have been submitted yet.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Survey Responses');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    const timestamp = new Date().toISOString().split('T')[0];
    saveAs(blob, `Survey_Responses_${this.name || this.contestID}_${timestamp}.xlsx`);
    this.sharedService.createNotification('success', 'Survey responses exported successfully!');
  }

  private parseSurveyAnswer(userAnswer: any, surveyQuestions: any[] = []): { question: string; answer: string }[] {
    if (!userAnswer) return [];
    if (typeof userAnswer === 'object') {
      return this.flattenSurveyAnswerObject(userAnswer, surveyQuestions);
    }
    if (typeof userAnswer === 'string') {
      const trimmed = userAnswer.trim();
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
          return this.flattenSurveyAnswerObject(JSON.parse(trimmed), surveyQuestions);
        } catch {
          // fallback to single text below
        }
      }
      if (surveyQuestions.length > 0) {
        const qText = this.isArabic
          ? (surveyQuestions[0]?.arabic_question_text || surveyQuestions[0]?.question_text)
          : (surveyQuestions[0]?.question_text || 'Question 1');
        return [{ question: qText, answer: trimmed }];
      }
      return [{ question: 'Survey Response', answer: trimmed }];
    }
    return [];
  }

  private flattenSurveyAnswerObject(parsed: any, surveyQuestions: any[] = []): { question: string; answer: string }[] {
    if (Array.isArray(parsed)) {
      return parsed.map((item: any, index: number) => {
        const fallbackQ = surveyQuestions[index]
          ? (this.isArabic ? (surveyQuestions[index]?.arabic_question_text || surveyQuestions[index]?.question_text) : surveyQuestions[index]?.question_text)
          : `Question ${index + 1}`;
        const question = item?.question || item?.question_text || item?.title || fallbackQ;
        const answer = item?.answer ?? item?.value ?? item?.user_answer ?? (typeof item === 'string' ? item : JSON.stringify(item));
        return { question, answer: String(answer ?? '-') };
      });
    }
    if (parsed && typeof parsed === 'object') {
      return Object.keys(parsed).map((key, index) => {
        const fallbackQ = surveyQuestions[index]
          ? (this.isArabic ? (surveyQuestions[index]?.arabic_question_text || surveyQuestions[index]?.question_text) : surveyQuestions[index]?.question_text)
          : key;
        const question = isNaN(Number(key)) ? key : fallbackQ;
        const val = parsed[key];
        const answer = typeof val === 'string' ? val : (val?.answer ?? val?.value ?? JSON.stringify(val));
        return { question, answer: String(answer ?? '-') };
      });
    }
    return [];
  }

  async exportToPDF(): Promise<void> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.width;
    let y = 0;

    const isAr = isArabicLang();
    const arabicFontLoaded = await ensureArabicFont(doc);

    const L = isAr ? {
      headerTitle: 'تقرير تفاصيل المشاركة',
      untitled: 'مشاركة بدون عنوان',
      uidLabel: 'المعرف',
      accessCodeLabel: 'رمز الدخول',
      startLabel: 'البداية',
      endLabel: 'النهاية',
      drawLabel: 'السحب',
      na: 'غير متوفر',
      prizesHeading: 'الجوائز',
      prizeNameCol: 'اسم الجائزة',
      valueCol: 'القيمة',
      winnersCol: 'الفائزون',
      drawTypeCol: 'نوع السحب',
      levelsHeading: 'مستويات المشاركة',
      numCol: '#',
      levelNameCol: 'اسم المستوى',
      descriptionCol: 'الوصف',
      pointsCol: 'النقاط',
      performanceHeading: 'أداء المشاركة',
      metricCol: 'المقياس',
      totalViews: 'إجمالي المشاهدات',
      totalClicks: 'إجمالي النقرات',
      participantsMetric: 'المشاركون',
      completed: 'مكتمل',
      overallSuccessRate: 'معدل النجاح الإجمالي',
      completionRate: 'معدل الإكمال',
      participationRate: 'معدل المشاركة',
      engagementRate: 'معدل التفاعل',
      participantsHeading: (count: number) => `المشاركون (${count})`,
      participantCol: 'المشارك',
      participationDateCol: 'تاريخ المشاركة',
      ageCol: 'العمر',
      genderCol: 'الجنس',
      statusCol: 'الحالة',
      locationCol: 'الموقع',
      statusCompleted: 'مكتمل',
      statusNotStarted: 'لم يبدأ',
      statusInProgress: 'قيد التقدم',
      noParticipants: 'لا توجد بيانات مشاركين متاحة.',
      winnersHeading: 'الفائزون',
      rankCol: 'الترتيب',
      completionTimestampCol: 'وقت الإكمال',
      prizeStatusCol: 'حالة الجائزة',
      noWinners: 'لا توجد بيانات فائزين متاحة.',
      previewHeading: 'معاينة الحملة (تطبيق الجوال)',
      pageOf: (p: number, total: number) => `صفحة ${p} من ${total}`,
      generated: (date: string) => `تم الإنشاء: ${date}`,
    } : {
      headerTitle: 'Campaign Details Report',
      untitled: 'Untitled Campaign',
      uidLabel: 'UID',
      accessCodeLabel: 'Access Code',
      startLabel: 'Start',
      endLabel: 'End',
      drawLabel: 'Draw',
      na: 'N/A',
      prizesHeading: 'Prizes',
      prizeNameCol: 'Prize Name',
      valueCol: 'Value',
      winnersCol: 'Winners',
      drawTypeCol: 'Draw Type',
      levelsHeading: 'Engagement Levels',
      numCol: '#',
      levelNameCol: 'Level Name',
      descriptionCol: 'Description',
      pointsCol: 'Points',
      performanceHeading: 'Engagement Performance',
      metricCol: 'Metric',
      totalViews: 'Total Views',
      totalClicks: 'Total Clicks',
      participantsMetric: 'Participants',
      completed: 'Completed',
      overallSuccessRate: 'Overall Success Rate',
      completionRate: 'Completion Rate',
      participationRate: 'Participation Rate',
      engagementRate: 'Engagement Rate',
      participantsHeading: (count: number) => `Participants (${count})`,
      participantCol: 'Participant',
      participationDateCol: 'Participation Date',
      ageCol: 'Age',
      genderCol: 'Gender',
      statusCol: 'Status',
      locationCol: 'Location',
      statusCompleted: 'Completed',
      statusNotStarted: 'Not Started',
      statusInProgress: 'In Progress',
      noParticipants: 'No participant data available.',
      winnersHeading: 'Winners',
      rankCol: 'Rank',
      completionTimestampCol: 'Completion Timestamp',
      prizeStatusCol: 'Prize Status',
      noWinners: 'No winners data available.',
      previewHeading: 'Contest Preview (Mobile App)',
      pageOf: (p: number, total: number) => `Page ${p} of ${total}`,
      generated: (date: string) => `Generated: ${date}`,
    };

    // Header bar
    doc.setFillColor(60, 52, 137);
    doc.rect(0, 0, pageWidth, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, L.headerTitle, pageWidth / 2, 14, arabicFontLoaded, { align: 'center' });
    y = 32;

    // Contest preview (banner image)
    const bannerUrl = this.banners?.[0]?.url || (this.banners?.[0] as any)?.path || '';
    const previewData = bannerUrl ? await this.sharedService.getImageDataUrl(bannerUrl) : null;
    if (previewData) {
      const previewWidth = pageWidth - 28;
      const previewHeight = 55;
      try {
        doc.addImage(previewData, 'JPEG', 14, y, previewWidth, previewHeight, undefined, 'FAST');
      } catch {
        // ignore image draw failures, continue with text-only report
      }
      y += previewHeight + 8;
    }

    // Contest name
    doc.setTextColor(30, 30, 30);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    drawBilingualText(doc, this.name || L.untitled, 14, y, arabicFontLoaded);
    y += 7;

    // UID / Access Code
    const idParts: string[] = [];
    if (this.hasValue(this.contests_detail?.uid)) idParts.push(`${L.uidLabel}: ${this.contests_detail.uid}`);
    if (this.hasValue(this.contests_detail?.access_code)) idParts.push(`${L.accessCodeLabel}: ${this.contests_detail.access_code}`);
    if (idParts.length) {
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(110, 110, 110);
      drawBilingualText(doc, idParts.join('   |   '), 14, y, arabicFontLoaded);
      y += 6;
    }

    // Description
    if (this.description) {
      const plainDesc = this.description.replace(/<[^>]*>/g, '').trim();
      if (plainDesc) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const lines = doc.splitTextToSize(plainDesc, pageWidth - 28);
        doc.text(lines, 14, y);
        y += lines.length * 5 + 3;
      }
    }

    // Dates row
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const fmt = (d: any) => d ? new Date(d).toLocaleString() : L.na;
    drawBilingualText(doc, `${L.startLabel}: ${fmt(this.start_date)}`, 14, y, arabicFontLoaded);
    drawBilingualText(doc, `${L.endLabel}: ${fmt(this.end_date)}`, 14, y + 5, arabicFontLoaded);
    drawBilingualText(doc, `${L.drawLabel}: ${fmt(this.draw_date)}`, 14, y + 10, arabicFontLoaded);
    y += 18;

    // Prizes
    if (this._contest_prize && this._contest_prize.length > 0) {
      y = this.pdfSectionHeading(doc, L.prizesHeading, y, arabicFontLoaded);
      const prizeRows = this._contest_prize.map((prize: any) => [
        prize?.name || L.na,
        prize?.value != null ? `${prize?.currency || 'USD'} ${prize.value}` : L.na,
        this.contests_detail?.number_of_winners != null ? String(this.contests_detail.number_of_winners) : L.na,
        this.getDrawTypeLabel ? this.getDrawTypeLabel() : (this.contests_detail?.draw_type || L.na),
      ]);
      autoTable(doc, {
        startY: y,
        head: [[L.prizeNameCol, L.valueCol, L.winnersCol, L.drawTypeCol]],
        body: prizeRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Contest Levels
    if (this.levels_of_contest && this.levels_of_contest.length > 0) {
      y = this.pdfEnsureSpace(doc, y, 30);
      y = this.pdfSectionHeading(doc, L.levelsHeading, y, arabicFontLoaded);

      const levelRows = this.levels_of_contest.map((lvl: any, i: number) => [
        String(i + 1),
        lvl.name || lvl.level_name || `Level ${i + 1}`,
        lvl.description || lvl.task || '',
        lvl.points != null ? String(lvl.points) : L.na,
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.numCol, L.levelNameCol, L.descriptionCol, L.pointsCol]],
        body: levelRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 45 }, 2: { cellWidth: 'auto' }, 3: { cellWidth: 20 } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    }

    // Mobile app preview mockup
    y = this.pdfEnsureSpace(doc, y, 140);
    y = this.pdfSectionHeading(doc, L.previewHeading, y, arabicFontLoaded);
    await this.drawMobilePreview(doc, y, isAr, arabicFontLoaded);
    y += 140;

    // Performance Overview (matches on-screen 8 metric cards)
    y = this.pdfEnsureSpace(doc, y, 60);
    y = this.pdfSectionHeading(doc, L.performanceHeading, y, arabicFontLoaded);

    autoTable(doc, {
      startY: y,
      head: [[L.metricCol, L.valueCol]],
      body: [
        [L.totalViews, String(this.performanceStats.totalContests ?? 0)],
        [L.totalClicks, String(this.performanceStats.activeContests ?? 0)],
        [L.participantsMetric, String(this.performanceStats.pendingContests ?? 0)],
        [L.completed, String(this.performanceStats.finishedContests ?? 0)],
        [L.overallSuccessRate, `${(this.performanceStats.avgClickRate ?? 0).toFixed(2)}%`],
        [L.completionRate, `${(this.performanceStats.avgCompletionRate ?? 0).toFixed(2)}%`],
        [L.participationRate, `${(this.performanceStats.avgParticipationRate ?? 0).toFixed(2)}%`],
        [L.engagementRate, `${(this.performanceStats.avgEngagementRate ?? 0).toFixed(2)}%`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 70 }, 1: { cellWidth: 'auto' } },
      margin: { left: 14, right: 14 },
      didParseCell: arabicAwareCellHook(arabicFontLoaded),
    });
    y = (doc as any).lastAutoTable.finalY + 10;

    // Participants
    y = this.pdfEnsureSpace(doc, y, 30);
    y = this.pdfSectionHeading(doc, L.participantsHeading(this.filteredParticipants?.length || 0), y, arabicFontLoaded);

    if (this.filteredParticipants && this.filteredParticipants.length > 0) {
      const participantRows = this.filteredParticipants.map((p: any) => [
        this.getParticipantName(p),
        this.getParticipationDate(p) ? new Date(this.getParticipationDate(p) as string).toLocaleString() : L.na,
        String(this.getParticipantAge(p)),
        this.getParticipantGender(p),
        p.status === 'completed' ? L.statusCompleted : (p.status === 'not_started' ? L.statusNotStarted : L.statusInProgress),
        this.getParticipantLocation(p),
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.participantCol, L.participationDateCol, L.ageCol, L.genderCol, L.statusCol, L.locationCol]],
        body: participantRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 140);
      drawBilingualText(doc, L.noParticipants, 14, y, arabicFontLoaded);
      y += 10;
    }

    // Winners
    y = this.pdfEnsureSpace(doc, y, 30);
    y = this.pdfSectionHeading(doc, L.winnersHeading, y, arabicFontLoaded);

    if (this.winnersList && this.winnersList.length > 0) {
      const winnerRows = this.winnersList.map((w: any, i: number) => [
        String(i + 1),
        w._end_user?.name || w.contest?.user_id || L.na,
        this.getParticipantGender(w),
        String(this.getParticipantAge(w)),
        this.getWinnerCompletionTimestamp(w) ? new Date(this.getWinnerCompletionTimestamp(w) as string).toLocaleString() : L.na,
        this.getWinnerPrizeStatus(w),
        w._end_user?.country || w.contest?.location || L.na,
      ]);

      autoTable(doc, {
        startY: y,
        head: [[L.rankCol, L.participantCol, L.genderCol, L.ageCol, L.completionTimestampCol, L.prizeStatusCol, L.locationCol]],
        body: winnerRows,
        theme: 'striped',
        headStyles: { fillColor: [8, 80, 65], textColor: 255, fontStyle: 'bold', fontSize: 8.5 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
    } else {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 140, 140);
      drawBilingualText(doc, L.noWinners, 14, y, arabicFontLoaded);
    }

    // Footer
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      drawBilingualText(doc, L.pageOf(p, totalPages), pageWidth / 2, doc.internal.pageSize.height - 6, arabicFontLoaded, { align: 'center' });
      drawBilingualText(doc, L.generated(new Date().toLocaleString()), 14, doc.internal.pageSize.height - 6, arabicFontLoaded);
    }

    const sanitizedName = (this.name || 'engagement').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
    const fileName = `${sanitizedName}_details.pdf`;
    doc.save(fileName);
  }

  private pdfImageFormat(dataUrl: string): string {
    if (dataUrl.startsWith('data:image/png')) return 'PNG';
    if (dataUrl.startsWith('data:image/webp')) return 'WEBP';
    return 'JPEG';
  }

  // Draws a modern, realistic phone-frame mockup of the contest mobile app preview.
  private async drawMobilePreview(doc: jsPDF, y: number, isAr: boolean = false, arabicFontLoaded: boolean = false): Promise<void> {
    const pageWidth = doc.internal.pageSize.width;
    const frameWidth = 74;
    const frameHeight = 144;
    const x = (pageWidth - frameWidth) / 2;

    // --- Phone Chassis (Outer dark titanium frame) ---
    doc.setFillColor(22, 24, 30);
    doc.roundedRect(x, y, frameWidth, frameHeight, 8, 8, 'F');
    doc.setDrawColor(55, 65, 81);
    doc.setLineWidth(0.7);
    doc.roundedRect(x, y, frameWidth, frameHeight, 8, 8, 'S');

    // Hardware buttons on edges
    doc.setFillColor(31, 35, 43);
    doc.roundedRect(x - 0.7, y + 24, 0.7, 8, 0.3, 0.3, 'F'); // Volume Up
    doc.roundedRect(x - 0.7, y + 35, 0.7, 8, 0.3, 0.3, 'F'); // Volume Down
    doc.roundedRect(x + frameWidth, y + 28, 0.7, 12, 0.3, 0.3, 'F'); // Power

    // --- Screen Display (Inner bezel) ---
    const screenX = x + 2.5;
    const screenY = y + 2.5;
    const screenWidth = frameWidth - 5;
    const screenHeight = frameHeight - 5;

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(screenX, screenY, screenWidth, screenHeight, 6, 6, 'F');

    // --- Dynamic Island / Top Notch ---
    const notchW = 22;
    const notchH = 3.6;
    const notchX = x + (frameWidth - notchW) / 2;
    const notchY = screenY + 2;

    doc.setFillColor(15, 17, 23);
    doc.roundedRect(notchX, notchY, notchW, notchH, 1.8, 1.8, 'F');
    // Camera lens dot & speaker
    doc.setFillColor(28, 32, 42);
    doc.circle(notchX + 17, notchY + 1.8, 0.7, 'F');
    doc.setFillColor(38, 42, 54);
    doc.roundedRect(notchX + 5, notchY + 1.4, 7, 0.6, 0.3, 0.3, 'F');

    // --- Status Bar (Time, Battery, Wifi) ---
    doc.setFontSize(5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 35);
    doc.text('9:41', screenX + 4, screenY + 4.5);

    // Battery & signal icons
    const statRightX = screenX + screenWidth - 11;
    doc.setFillColor(30, 30, 35);
    doc.roundedRect(statRightX + 4, screenY + 2.8, 4.5, 2.2, 0.4, 0.4, 'S');
    doc.roundedRect(statRightX + 4.5, screenY + 3.2, 2.5, 1.4, 0.2, 0.2, 'F');
    doc.roundedRect(statRightX + 8.5, screenY + 3.4, 0.5, 1, 0.2, 0.2, 'F');
    // Wifi bars
    doc.circle(statRightX + 1.5, screenY + 4.2, 0.6, 'F');

    // --- In-App Top Navigation Bar ---
    const navY = screenY + 7.5;
    doc.setFillColor(243, 244, 246);
    doc.circle(screenX + 5, navY + 2, 2.5, 'F');
    doc.setFontSize(5.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(55, 65, 81);
    doc.text('‹', screenX + 4.3, navY + 2.8);

    const typeBadge = String(this.contests_detail?.type || 'Contest').toUpperCase();
    doc.setFillColor(238, 235, 255);
    doc.roundedRect(screenX + screenWidth - 22, navY, 17, 4.2, 2, 2, 'F');
    doc.setTextColor(83, 4, 251);
    doc.setFontSize(4.5);
    doc.text(typeBadge, screenX + screenWidth - 13.5, navY + 2.9, { align: 'center' });

    // --- Contest Banner Hero Area ---
    const bannerX = screenX + 3;
    const bannerY = navY + 6;
    const bannerW = screenWidth - 6;
    const bannerH = 44;

    const bannerUrl = this.banners && this.banners.length > 0 ? (this.banners[0].url || (this.banners[0] as any).path) : null;
    let bannerDrawn = false;
    if (bannerUrl) {
      try {
        const dataUrl = await this.sharedService.getImageDataUrl(bannerUrl);
        if (dataUrl) {
          doc.addImage(dataUrl, this.pdfImageFormat(dataUrl), bannerX, bannerY, bannerW, bannerH);
          bannerDrawn = true;
        }
      } catch {
        bannerDrawn = false;
      }
    }

    if (!bannerDrawn) {
      doc.setFillColor(60, 52, 137);
      doc.roundedRect(bannerX, bannerY, bannerW, bannerH, 4, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('LuQta Campaign', bannerX + bannerW / 2, bannerY + bannerH / 2, { align: 'center' });
    } else {
      // Crisp subtle border around banner
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.3);
      doc.roundedRect(bannerX, bannerY, bannerW, bannerH, 3, 3, 'S');
    }

    // Floating Active Status Badge on Banner
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(bannerX + 3, bannerY + 3, 14, 4, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(4.5);
    doc.setFont('helvetica', 'bold');
    doc.text('ACTIVE', bannerX + 10, bannerY + 5.8, { align: 'center' });

    // --- Content Details (Title, Description, Stats) ---
    let contentCursorY = bannerY + bannerH + 4;
    const contentX = screenX + 4;
    const contentW = screenWidth - 8;

    const rawTitle = this.name || (isAr ? 'الحملة' : 'Campaign');
    const isArabicTitle = containsArabic(rawTitle);

    if (isArabicTitle) {
      const titleImg = renderArabicTextAsImage(rawTitle, contentW, 8.5, '#111827', isAr ? 'right' : 'left', 'bold', 2);
      if (titleImg) {
        const imgX = isAr ? (contentX + contentW - titleImg.widthMm) : contentX;
        doc.addImage(titleImg.dataUrl, 'PNG', imgX, contentCursorY, titleImg.widthMm, titleImg.heightMm);
        contentCursorY += titleImg.heightMm + 2.5;
      } else {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(17, 24, 39);
        drawBilingualText(doc, rawTitle, isAr ? contentX + contentW : contentX, contentCursorY + 3, arabicFontLoaded, { align: isAr ? 'right' : 'left' });
        contentCursorY += 6;
      }
    } else {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(17, 24, 39);
      const titleLines = doc.splitTextToSize(rawTitle, contentW).slice(0, 2);
      doc.text(titleLines, contentX, contentCursorY + 3);
      contentCursorY += titleLines.length * 3.8 + 2;
    }

    // Description
    const rawDesc = (this.description || '').replace(/<[^>]*>/g, '').trim() || (isAr ? 'شارك في هذه المسابقة واربح جوائز قيمة.' : 'Participate in this contest to win exciting prizes.');
    const isArabicDesc = containsArabic(rawDesc);

    if (isArabicDesc) {
      const descImg = renderArabicTextAsImage(rawDesc, contentW, 6, '#4B5563', isAr ? 'right' : 'left', 'normal', 3);
      if (descImg) {
        const imgX = isAr ? (contentX + contentW - descImg.widthMm) : contentX;
        doc.addImage(descImg.dataUrl, 'PNG', imgX, contentCursorY, descImg.widthMm, descImg.heightMm);
        contentCursorY += descImg.heightMm + 3;
      } else {
        doc.setFontSize(6);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(75, 85, 99);
        drawBilingualText(doc, rawDesc.slice(0, 70), isAr ? contentX + contentW : contentX, contentCursorY + 2.5, arabicFontLoaded, { align: isAr ? 'right' : 'left' });
        contentCursorY += 6;
      }
    } else {
      doc.setFontSize(6);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(75, 85, 99);
      const descLines = doc.splitTextToSize(rawDesc, contentW).slice(0, 3);
      doc.text(descLines, contentX, contentCursorY + 2.5);
      contentCursorY += descLines.length * 3 + 2;
    }

    // Mini feature badges (Levels & Prizes)
    const levelsCount = this.levels_of_contest?.length || 1;
    const badgeW = (contentW - 3) / 2;
    const badgeRowY = Math.min(contentCursorY + 1, screenY + screenHeight - 24);

    // Levels Pill
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(contentX, badgeRowY, badgeW, 6, 2, 2, 'F');
    doc.setTextColor(55, 65, 81);
    doc.setFontSize(5);
    doc.setFont('helvetica', 'bold');
    doc.text(`${levelsCount} ${isAr ? 'مستويات' : 'Levels'}`, contentX + badgeW / 2, badgeRowY + 4, { align: 'center' });

    // Prize Pill
    const prizeText = this._contest_prize?.[0]?.name ? `${this._contest_prize[0].name}` : (isAr ? 'جوائز كبرى' : 'Grand Prizes');
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(contentX + badgeW + 3, badgeRowY, badgeW, 6, 2, 2, 'F');
    if (containsArabic(prizeText)) {
      const pImg = renderArabicTextAsImage(prizeText, badgeW - 2, 5, '#374151', 'center', 'bold', 1);
      if (pImg) {
        doc.addImage(pImg.dataUrl, 'PNG', contentX + badgeW + 3 + (badgeW - pImg.widthMm) / 2, badgeRowY + (6 - pImg.heightMm) / 2, pImg.widthMm, pImg.heightMm);
      }
    } else {
      doc.setTextColor(55, 65, 81);
      doc.setFontSize(5);
      doc.setFont('helvetica', 'bold');
      doc.text(prizeText.slice(0, 15), contentX + badgeW + 3 + badgeW / 2, badgeRowY + 4, { align: 'center' });
    }

    // --- Call to Action Button (Participate Now) ---
    const ctaW = screenWidth - 8;
    const ctaH = 8.5;
    const ctaX = screenX + 4;
    const ctaY = screenY + screenHeight - 15;

    doc.setFillColor(83, 4, 251);
    doc.roundedRect(ctaX, ctaY, ctaW, ctaH, 4, 4, 'F');

    const ctaText = isAr ? 'شارك الآن' : 'Participate Now';
    if (isAr || containsArabic(ctaText)) {
      const ctaImg = renderArabicTextAsImage(ctaText, ctaW - 4, 7.5, '#FFFFFF', 'center', 'bold', 1);
      if (ctaImg) {
        doc.addImage(ctaImg.dataUrl, 'PNG', ctaX + (ctaW - ctaImg.widthMm) / 2, ctaY + (ctaH - ctaImg.heightMm) / 2, ctaImg.widthMm, ctaImg.heightMm);
      } else {
        doc.setFontSize(7.5);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        drawBilingualText(doc, ctaText, ctaX + ctaW / 2, ctaY + 5.5, arabicFontLoaded, { align: 'center' });
      }
    } else {
      doc.setFontSize(7.5);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.text(ctaText, ctaX + ctaW / 2, ctaY + 5.5, { align: 'center' });
    }

    // --- Home Indicator Bar ---
    doc.setFillColor(209, 213, 219);
    doc.roundedRect(x + (frameWidth - 26) / 2, screenY + screenHeight - 3.5, 26, 1.2, 0.6, 0.6, 'F');
  }

  // Contest configuration PDF: all creation-form fields, Luqta/client logos, a mobile-app
  // preview mockup, and submission metadata (submitted by/via/on, status, approved by).
  async exportConfigurationPDF(): Promise<void> {
    this.sharedService.showLoader();
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;
      let y = 0;

      const isAr = isArabicLang();
      const arabicFontLoaded = await ensureArabicFont(doc);

      const L = isAr ? {
        headerTitle: 'تفاصيل إعدادات المسابقة',
        untitled: 'مسابقة بدون عنوان',
        submissionDetailsHeading: 'تفاصيل التقديم',
        fieldCol: 'الحقل',
        valueCol: 'القيمة',
        submittedBy: 'مقدم بواسطة',
        submittedVia: 'تم التقديم عبر',
        submittedOn: 'تاريخ التقديم',
        status: 'الحالة',
        approvedBy: 'تمت الموافقة بواسطة',
        notYetApproved: 'لم تتم الموافقة بعد',
        na: 'غير متوفر',
        descriptionHeading: 'الوصف',
        configurationHeading: 'الإعدادات',
        type: 'النوع',
        format: 'الصيغة',
        sharingSetting: 'إعداد المشاركة',
        gender: 'الجنس',
        ageGroup: 'الفئة العمرية',
        location: 'الموقع',
        businessObjective: 'الهدف التجاري',
        numberOfWinners: 'عدد الفائزين',
        drawType: 'نوع السحب',
        pointsPoolPerContest: 'مجموع النقاط لكل مسابقة',
        noOfUsersSdk: 'عدد المستخدمين (SDK)',
        startDate: 'تاريخ البداية',
        endDate: 'تاريخ النهاية',
        drawDate: 'تاريخ السحب',
        prizesHeading: 'الجوائز',
        prizeNameCol: 'اسم الجائزة',
        levelsHeading: 'مستويات المشاركة',
        numCol: '#',
        levelNameCol: 'اسم المستوى',
        typeCol: 'النوع',
        pointsCol: 'النقاط',
        previewHeading: 'معاينة الحملة (تطبيق الجوال)',
        pageOf: (p: number, total: number) => `صفحة ${p} من ${total}`,
        generated: (date: string) => `تم الإنشاء: ${date}`,
      } : {
        headerTitle: 'Campaign Configuration Details',
        untitled: 'Untitled Campaign',
        submissionDetailsHeading: 'Submission Details',
        fieldCol: 'Field',
        valueCol: 'Value',
        submittedBy: 'Submitted By',
        submittedVia: 'Submitted Via',
        submittedOn: 'Submitted On',
        status: 'Status',
        approvedBy: 'Approved By',
        notYetApproved: 'Not yet approved',
        na: 'N/A',
        descriptionHeading: 'Description',
        configurationHeading: 'Configuration',
        type: 'Type',
        format: 'Format',
        sharingSetting: 'Sharing Setting',
        gender: 'Gender',
        ageGroup: 'Age Group',
        location: 'Location',
        businessObjective: 'Business Objective',
        numberOfWinners: 'Number of Winners',
        drawType: 'Draw Type',
        pointsPoolPerContest: 'Points Pool Per Contest',
        noOfUsersSdk: 'No. of Users (SDK)',
        startDate: 'Start Date',
        endDate: 'End Date',
        drawDate: 'Draw Date',
        prizesHeading: 'Prizes',
        prizeNameCol: 'Prize Name',
        levelsHeading: 'Engagement Levels',
        numCol: '#',
        levelNameCol: 'Level Name',
        typeCol: 'Type',
        pointsCol: 'Points',
        previewHeading: 'Contest Preview (Mobile App)',
        pageOf: (p: number, total: number) => `Page ${p} of ${total}`,
        generated: (date: string) => `Generated: ${date}`,
      };

      const [luqtaLogo, clientLogo] = await Promise.all([
        this.sharedService.getLuqtaLogoDataUrl(),
        this.sharedService.getClientLogoDataUrl(),
      ]);

      // Header bar with Luqta logo (left) and client logo (right)
      doc.setFillColor(60, 52, 137);
      doc.rect(0, 0, pageWidth, 26, 'F');
      if (luqtaLogo) {
        try { doc.addImage(luqtaLogo, this.pdfImageFormat(luqtaLogo), 10, 4, 18, 18); } catch { }
      }
      if (clientLogo) {
        try { doc.addImage(clientLogo, this.pdfImageFormat(clientLogo), pageWidth - 28, 4, 18, 18); } catch { }
      }
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, L.headerTitle, pageWidth / 2, 15, arabicFontLoaded, { align: 'center' });
      y = 36;

      const cd = this.contests_detail || {};

      doc.setTextColor(30, 30, 30);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      drawBilingualText(doc, this.name || L.untitled, 14, y, arabicFontLoaded);
      y += 8;

      // Submission details
      y = this.pdfSectionHeading(doc, L.submissionDetailsHeading, y, arabicFontLoaded);
      autoTable(doc, {
        startY: y,
        head: [[L.fieldCol, L.valueCol]],
        body: [
          [L.submittedBy, this.submitted_by?.name ? `${this.submitted_by.name}${this.submitted_by.email ? ' (' + this.submitted_by.email + ')' : ''}` : L.na],
          [L.submittedVia, this.submitted_via || L.na],
          [L.submittedOn, this.submitted_on || L.na],
          [L.status, this.getStatusLabel(cd.status)],
          [L.approvedBy, this.approved_by?.name ? `${this.approved_by.name}${this.approved_by.email ? ' (' + this.approved_by.email + ')' : ''}` : L.notYetApproved],
        ],
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Description
      y = this.pdfEnsureSpace(doc, y, 30);
      y = this.pdfSectionHeading(doc, L.descriptionHeading, y, arabicFontLoaded);
      const plainDesc = (this.description || '').replace(/<[^>]*>/g, '').trim() || L.na;
      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      if (containsArabic(plainDesc) && arabicFontLoaded) {
        doc.setFont('Amiri', 'normal');
        const descLines = doc.splitTextToSize(reshapeArabic(plainDesc), pageWidth - 28);
        doc.text(descLines, pageWidth - 14, y, { align: 'right' });
        y += descLines.length * 5 + 8;
      } else {
        doc.setFont('helvetica', 'normal');
        const descLines = doc.splitTextToSize(plainDesc, pageWidth - 28);
        doc.text(descLines, 14, y);
        y += descLines.length * 5 + 8;
      }

      // Configuration fields (from the contest creation form)
      y = this.pdfEnsureSpace(doc, y, 80);
      y = this.pdfSectionHeading(doc, L.configurationHeading, y, arabicFontLoaded);
      const businessObjective = Array.isArray(cd.business_objective)
        ? cd.business_objective.join(', ')
        : (cd.business_objective || L.na);
      const configRows: string[][] = [
        [L.type, cd.type || L.na],
        [L.format, cd.format_of_contest || L.na],
        [L.sharingSetting, cd.sharingSetting || cd.sharing_setting || L.na],
        [L.gender, cd.gender || L.na],
        [L.ageGroup, cd.age || L.na],
        [L.location, cd.location || L.na],
        [L.businessObjective, businessObjective],
        [L.numberOfWinners, cd.number_of_winners != null ? String(cd.number_of_winners) : L.na],
        [L.drawType, this.getDrawTypeLabel()],
        [L.pointsPoolPerContest, cd.points_pool_per_contest != null ? String(cd.points_pool_per_contest) : L.na],
        [L.noOfUsersSdk, cd.no_of_users != null ? String(cd.no_of_users) : L.na],
        [L.startDate, this.start_date || L.na],
        [L.endDate, this.end_date || L.na],
        [L.drawDate, this.draw_date || L.na],
      ];
      autoTable(doc, {
        startY: y,
        head: [[L.fieldCol, L.valueCol]],
        body: configRows,
        theme: 'striped',
        headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 55 }, 1: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
        didParseCell: arabicAwareCellHook(arabicFontLoaded),
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Prizes
      if (this._contest_prize && this._contest_prize.length > 0) {
        y = this.pdfEnsureSpace(doc, y, 30);
        y = this.pdfSectionHeading(doc, L.prizesHeading, y, arabicFontLoaded);
        const prizeRows = this._contest_prize.map((prize: any) => [
          prize?.name || L.na,
          prize?.value != null ? `${prize?.currency || 'USD'} ${prize.value}` : L.na,
        ]);
        autoTable(doc, {
          startY: y,
          head: [[L.prizeNameCol, L.valueCol]],
          body: prizeRows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
          didParseCell: arabicAwareCellHook(arabicFontLoaded),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Levels
      if (this.levels_of_contest && this.levels_of_contest.length > 0) {
        y = this.pdfEnsureSpace(doc, y, 30);
        y = this.pdfSectionHeading(doc, L.levelsHeading, y, arabicFontLoaded);
        const levelRows = this.levels_of_contest.map((lvl: any, i: number) => [
          String(i + 1),
          lvl.name || lvl.level_name || `Level ${i + 1}`,
          lvl.level_type || lvl.type || L.na,
          lvl.points != null ? String(lvl.points) : L.na,
        ]);
        autoTable(doc, {
          startY: y,
          head: [[L.numCol, L.levelNameCol, L.typeCol, L.pointsCol]],
          body: levelRows,
          theme: 'striped',
          headStyles: { fillColor: [60, 52, 137], textColor: 255, fontStyle: 'bold', fontSize: 9 },
          bodyStyles: { fontSize: 9 },
          margin: { left: 14, right: 14 },
          didParseCell: arabicAwareCellHook(arabicFontLoaded),
        });
        y = (doc as any).lastAutoTable.finalY + 10;
      }

      // Mobile app preview mockup
      y = this.pdfEnsureSpace(doc, y, 140);
      y = this.pdfSectionHeading(doc, L.previewHeading, y, arabicFontLoaded);
      await this.drawMobilePreview(doc, y, isAr, arabicFontLoaded);

      // Footer
      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        drawBilingualText(doc, L.pageOf(p, totalPages), pageWidth / 2, doc.internal.pageSize.height - 6, arabicFontLoaded, { align: 'center' });
        drawBilingualText(doc, L.generated(new Date().toLocaleString()), 14, doc.internal.pageSize.height - 6, arabicFontLoaded);
      }

      const sanitizedName = (this.name || 'contest').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
      doc.save(`${sanitizedName}_configuration.pdf`);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to export configuration PDF.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  ngOnInit(): void {
    // Setup debounced search for participants
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyClientFilters();
    });

    const encodedUid = this.route.snapshot.queryParamMap.get('id');

    if (encodedUid) {
      const storedId = localStorage.getItem(encodedUid);

      if (storedId) {
        this.contestID = Number(storedId);
        this.getContestPerformance(this.contestID);
        this.getContestDetails(this.contestID);
        this.getLevelCompletionMetrics(this.contestID);
      } else {
        this.sharedService.createNotification('error', this.translateService.instant('contestDetails.invalidOrExpiredLink'));
        this.router.navigate(['/home/contests']);
      }
    } else {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.noContestIdInUrl'));
      this.router.navigate(['/home/contests']);
    }
  }

  async getLevelCompletionMetrics(contestId: number): Promise<void> {
    this.levelMetricsLoading = true;
    this.levelMetricsError = '';
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEVEL_COMPLETION_METRICS', { contest_id: contestId, qr_instance_id: 0 })
      );
      const payload = response?.data ?? response;
      if (payload && Array.isArray(payload.levels)) {
        payload.levels = payload.levels.map((lvl: any) => {
          const completed = Number(lvl.completed_count ?? lvl.completed ?? 0);
          const inProgress = Number(lvl.in_progress_count ?? lvl.in_progress ?? 0);
          const notStarted = Number(lvl.not_started_count ?? lvl.not_started ?? 0);
          const total = completed + inProgress + notStarted;
          let completionRate = Number(lvl.completion_rate ?? lvl.completion_rate_pct ?? lvl.completionRate ?? 0);
          if (completionRate === 0 && total > 0 && completed > 0) {
            completionRate = Math.round((completed / total) * 100 * 10) / 10;
          }
          return {
            ...lvl,
            completed_count: completed,
            in_progress_count: inProgress,
            not_started_count: notStarted,
            completion_rate: completionRate
          };
        });
        this.levelMatrix = payload;
      } else {
        this.levelMatrix = null;
      }
    } catch (error: any) {
      this.levelMetricsError = 'Level metrics unavailable.';
      this.levelMatrix = null;
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch level completion metrics:', error);
      }
    } finally {
      this.levelMetricsLoading = false;
    }
  }

  // Opens the demographic breakdown drawer for a single level row. The level
  // object already carries by_gender/by_country/by_age_group/by_device_type
  // from the contest_level_completion_metrics response, so no extra fetch.
  openLevelDemographicsDrawer(level: any): void {
    this.selectedLevelMetrics = level;
    this.levelDemographicsDrawerVisible = true;
  }

  closeLevelDemographicsDrawer(): void {
    this.levelDemographicsDrawerVisible = false;
    this.selectedLevelMetrics = null;
  }

  // "unknown" (missing profile field) is rendered as its own labeled bucket
  // rather than dropped, per each dimension's breakdown array.
  getBreakdownLabel(value: any): string {
    if (value === null || value === undefined) return 'Unknown';
    const normalized = String(value).trim();
    if (!normalized || normalized.toLowerCase() === 'unknown') return 'Unknown';
    return normalized;
  }

  getDemographicSections(level: any): Array<{ key: string; title: string; dimensionLabel: string; icon: string; rows: any[]; totalCount: number }> {
    if (!level) return [];

    // Rows are sorted highest-total-first so a later top-N slice (e.g. country) keeps the most relevant groups.
    const buildRows = (entries: any[], dimensionKey: string) =>
      (Array.isArray(entries) ? entries : []).map((entry: any) => {
        const notStarted = entry?.not_started ?? 0;
        const inProgress = entry?.in_progress ?? 0;
        const completed = entry?.completed ?? 0;
        return {
          label: this.getBreakdownLabel(entry?.[dimensionKey]),
          not_started: notStarted,
          in_progress: inProgress,
          completed: completed,
          total: notStarted + inProgress + completed
        };
      }).sort((a, b) => b.total - a.total);

    // Gender and age-group breakdowns are gone — Web QR collects neither.
    const countryRows = buildRows(level.by_country, 'country');
    const deviceTypeRows = buildRows(level.by_device_type, 'device_type');

    return [
      // Country lists can grow large, so only the top 5 by participant count are shown.
      { key: 'country', title: this.translateService.instant('contestDetails.byCountryTop5'), dimensionLabel: this.translateService.instant('contestDetails.country'), icon: 'fa-earth-americas', rows: countryRows.slice(0, 5), totalCount: countryRows.length },
      { key: 'device_type', title: this.translateService.instant('contestDetails.byDeviceType'), dimensionLabel: this.translateService.instant('contestDetails.deviceType'), icon: 'fa-mobile-screen-button', rows: deviceTypeRows, totalCount: deviceTypeRows.length },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editContest() {
    if (!this.isContestEditable()) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.cannotEditApprovedCompleted'));
      return;
    }

    const id = this.contestID;
    const uid = uuidv4(); // Generate a unique UID for this contest

    // Store the mapping temporarily (UID → actual numeric ID)
    localStorage.setItem(uid, id.toString());

    // Navigate using the UID instead of the real numeric ID
    this.router.navigate(['/home/contests/edit'], { queryParams: { id: uid } });
  }

  isContestEditable(): boolean {
    if (!this.contests_detail || !this.contests_detail.status) {
      return true; // Allow edit if status is not available
    }

    const status = this.contests_detail.status.toLowerCase();
    return status !== 'approved' && status !== 'complete' && status !== 'completed';
  }

  viewContestAnalytics() {
    this.router.navigate(['/home/analytic/monitoring'], {
      queryParams: { contest_id: this.contestID },
    });
  }
  async getContestDetails(id: string) {
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(this.pagesService.getById('GET_SINGLE_CONTEST', id));
      if (response) {
        const contestData = response;
        this.contests_detail = contestData;
        this.name = contestData.name;
        this.arabic_name = contestData.arabic_name || '';
        this.created_at = contestData.created_at;
        this.description = contestData.details;
        this.arabic_description = contestData.arabic_details || '';

        this.submitted_by = contestData.submitted_by ?? null;
        this.submitted_via = contestData.submitted_via ?? null;
        // submitted_on has no dedicated backend field yet — falls back to created_at.
        this.submitted_on = this.formatDate(contestData.submitted_on ?? contestData.created_at);
        this.approved_by = contestData.approved_by ?? null;

        // Sort levels by their order if available, otherwise preserve backend sequence
        const normalizedLevels = contestData._levels_of_contest
          .map((lvl: any) => this.normalizeLevelForDisplay(lvl));
        const hasExplicitOrder = normalizedLevels.some((lvl: any) => lvl?.order !== undefined && lvl?.order !== null);
        this.levels_of_contest = hasExplicitOrder
          ? [...normalizedLevels].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
          : normalizedLevels;

        this.banners = contestData.banners;
        this.start_date = this.formatDate(contestData.start_date);
        this.end_date = this.formatDate(contestData.end_date);
        this.draw_date = this.resolveDrawDate(contestData);
        this._contest_prize = contestData._contest_prize;

        this.levels_of_contest.forEach(level => {
          this.showAllVariables[level.id] = false;
        });

        // Pre-load quizzes if any level has quiz type
        const hasQuizLevel = this.levels_of_contest.some((level: any) =>
          (level.level_type || level.type) === 'quiz'
        );
        if (hasQuizLevel && !this.quizzesLoaded) {
          this.getQuizzesRecords();
        }

        // Fetch participants data and analytics for this contest
        await this.getContestParticipants(id);
        await this.getContestAnalytics(id);
        await this.buildLeaderboard();

        this.applyPerformanceFallbacks();
      }
    } catch (error) {
      this.sharedService.showError(error, 'Failed to fetch campaign details.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  async getContestParticipants(contestId: string) {
    this.participantsLoading = true;
    this.winnersLoading = true;
    try {
      // Use GET request with query parameter instead of path parameter
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('GET_Total_PARTICIPANT', 'contest_id', contestId)
      );

      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : [];

      if (items.length > 0) {
        const normalizedParticipants = items.map((participant: any) =>
          this.normalizeContestParticipant(participant)
        );

        const sortedParticipants = [...normalizedParticipants].sort(
          (a: any, b: any) =>
            this.getParticipationDateTimestamp(b) - this.getParticipationDateTimestamp(a)
        );

        this.participantsList = sortedParticipants;
        this.filteredParticipants = sortedParticipants;

        // Extract winners from participants
        this.extractWinners();
        this.applyPerformanceFallbacks();
      } else {
        // If response is not an array, set empty arrays
        this.participantsList = [];
        this.filteredParticipants = [];
        this.winnersList = [];
        this.applyPerformanceFallbacks();
      }
    } catch (error: any) {
      // Handle error silently - just set empty arrays
      this.participantsList = [];
      this.filteredParticipants = [];
      this.winnersList = [];
      this.applyPerformanceFallbacks();

      // Don't show error notification for ERROR_CODE_NOT_FOUND
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch participants:', error);
      }
    } finally {
      this.participantsLoading = false;
      this.winnersLoading = false;
    }
  }

  async getContestAnalytics(contestId: string) {
    this.analyticsLoading = true;
    try {
      let response: any;
      try {
        response = await firstValueFrom(
          this.pagesService.getAll('QR_REPORT_DETAIL', { contest_id: Number(contestId), qr_instance_id: 0 })
        );
      } catch {
        response = await firstValueFrom(
          this.pagesService.getByIdByQuerry('ANALYSIS_URL_Reports_DETAIL', contestId)
        );
      }
      const dataArray = Array.isArray(response) ? response : (response?.data || response?.items || response?.result || []);
      this.analyticsActivity = Array.isArray(dataArray) ? dataArray : [];
    } catch (error: any) {
      this.analyticsActivity = [];
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest analytics:', error);
      }
    } finally {
      this.analyticsLoading = false;
      this.analyticsLoaded = true;
      this.applyPerformanceFallbacks();
    }
  }

  private normalizeContestParticipant(participant: any): any {
    if (!participant || typeof participant !== 'object') {
      return participant;
    }

    const normalized = { ...participant };

    const contestCandidate =
      participant?.contest ||
      participant?._contest ||
      participant?.contest_detail ||
      participant?.contestDetail ||
      participant?.contest_details ||
      participant?.contestDetails ||
      this.contests_detail ||
      null;

    if (contestCandidate && !participant?.contest) {
      normalized.contest = contestCandidate;
    }

    const endUserCandidate =
      participant?._end_user ||
      participant?.end_user ||
      participant?.endUser ||
      participant?.user ||
      participant?.participant ||
      null;

    if (endUserCandidate && !participant?._end_user) {
      normalized._end_user = endUserCandidate;
    }

    const statusCandidate =
      participant?.status ||
      participant?.participant_status ||
      participant?.contestParticipant?.status ||
      participant?.contest_participant?.[0]?.status ||
      participant?._contest_participant?.[0]?.status ||
      participant?.contest_participants?.[0]?.status ||
      null;

    if (statusCandidate && !participant?.status) {
      normalized.status = statusCandidate;
    }

    if (statusCandidate && !participant?.participant_status) {
      normalized.participant_status = statusCandidate;
    }

    if (!participant?.levels_detail && participant?.levelsDetail) {
      normalized.levels_detail = participant.levelsDetail;
    }

    if (!participant?.levelsDetail && participant?.levels_detail) {
      normalized.levelsDetail = participant.levels_detail;
    }

    return normalized;
  }

  private getParticipationDateTimestamp(participant: any): number {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    if (!participationDate) {
      return 0;
    }

    const timestamp = new Date(participationDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  getParticipationDate(participant: any): string | null {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    return participationDate || null;
  }

  getParticipantName(participant: any): string {
    return (
      participant?._end_user?.name ||
      participant?.end_user?.name ||
      participant?.participant_name ||
      participant?.userName ||
      participant?.user_name ||
      participant?.name ||
      participant?.end_user?.full_name ||
      participant?._end_user?.full_name ||
      participant?.end_user?.phone_number ||
      participant?.phone ||
      participant?.email ||
      (participant?.id ? `Participant #${participant.id}` : 'User')
    );
  }

  getParticipantGender(participant: any): string {
    return (
      participant?._end_user?.gender ||
      participant?.end_user?.gender ||
      participant?.participant_gender ||
      participant?.participant_gender ||
      participant?.gender ||
      'N/A'
    );
  }

  getParticipantLocation(participant: any): string {
    return (
      participant?._end_user?.country ||
      participant?.end_user?.country ||
      participant?.participant_country ||
      participant?.country ||
      'N/A'
    );
  }

  getParticipantAge(participant: any): string | number {
    const directAge =
      participant?._end_user?.age ??
      participant?.end_user?.age ??
      participant?.participant_age ??
      participant?.age;

    if (directAge !== null && directAge !== undefined && directAge !== '') {
      return directAge;
    }

    const dob =
      participant?._end_user?.date_of_birth ||
      participant?._end_user?.dob ||
      participant?.end_user?.date_of_birth ||
      participant?.end_user?.dob ||
      participant?.participant_dob ||
      participant?.date_of_birth ||
      participant?.dob;

    if (!dob) {
      return 'N/A';
    }

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) {
      return 'N/A';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : 'N/A';
  }

  getWinnerCompletionTimestamp(winner: any): string | null {
    const completionTimestamp =
      winner?.completed_at ||
      winner?.completion_date ||
      winner?.completion_timestamp ||
      winner?.updated_at ||
      winner?.modified_at ||
      null;

    return completionTimestamp || null;
  }

  getWinnerPrizeStatus(winner: any): string {
    const participantId = winner?.participant_id ?? winner?._end_user?.id;
    const nestedPrizeHistoryStatus =
      winner?.contest?.prizes?.[0]?.prize_history?.find(
        (history: any) => String(history?.end_user_id) === String(participantId)
      )?.status;

    return (
      winner?.prize_status ||
      winner?.prizeStatus ||
      nestedPrizeHistoryStatus ||
      (winner?.is_winner ? 'Winner' : 'N/A')
    );
  }

  // Display label for the prize status badge — translates only the client-added 'Winner' fallback,
  // leaving raw backend status values (pending, shipped, etc.) untouched so getWinnerPrizeStatusClass matching still works.
  getWinnerPrizeStatusDisplay(winner: any): string {
    const status = this.getWinnerPrizeStatus(winner);
    return status === 'Winner' ? this.translateService.instant('contestDetails.won') : status;
  }

  getWinnerPrizeStatusClass(winner: any): string {
    const rawStatus = (this.getWinnerPrizeStatus(winner) || '').toLowerCase().trim();
    const status = rawStatus.replace(/_/g, ' ');

    if (status === 'pending') return 'prize-status pending';
    if (status === 'processing') return 'prize-status processing';
    if (status === 'ready for dispatch') return 'prize-status ready-dispatch';
    if (status === 'shipped') return 'prize-status shipped';
    if (status === 'in transit') return 'prize-status in-transit';
    if (status === 'out for delivery') return 'prize-status out-delivery';
    if (status === 'delivered' || status === 'completed' || status === 'winner') return 'prize-status delivered';
    if (status === 'returned') return 'prize-status returned';
    if (status === 'rejected' || status === 'cancelled' || status === 'failed') return 'prize-status failed';

    return 'prize-status default';
  }

  extractWinners() {
    this.winnersList = [];

    // Winner source can come from single contest payload OR nested inside participant.contest.
    const contestDraws = Array.isArray(this.contests_detail?.contest_draw)
      ? this.contests_detail.contest_draw
      : Array.isArray(this.contests_detail?._contest_draw)
        ? this.contests_detail._contest_draw
        : Array.isArray(this.participantsList?.[0]?.contest?.contest_draw)
          ? this.participantsList[0].contest.contest_draw
          : [];

    const winnerIds = new Set<string>();
    if (Array.isArray(contestDraws)) {
      contestDraws.forEach((draw: any) => {
        const rawWinners = draw?.winner_of_contest_draw || draw?.winners || draw?.winner_list || [];
        if (Array.isArray(rawWinners)) {
          rawWinners.forEach((winner: any) => {
            const wId = winner?.winner_id ?? winner?.participant_id ?? winner?.id ?? winner?.user_id;
            if (wId !== null && wId !== undefined) {
              winnerIds.add(String(wId));
            }
          });
        }
      });
    }

    // Also check direct winners array on contest payload
    const directContestWinners = this.contests_detail?.winners || this.contests_detail?.winner_list || this.contests_detail?._winners;
    if (Array.isArray(directContestWinners)) {
      directContestWinners.forEach((w: any) => {
        const wId = w?.winner_id ?? w?.participant_id ?? w?.id ?? w?.user_id;
        if (wId !== null && wId !== undefined) {
          winnerIds.add(String(wId));
        }
      });
    }

    if (winnerIds.size > 0 && this.participantsList.length > 0) {
      this.winnersList = this.participantsList.filter((participant: any) => {
        const participantId =
          participant?.participant_id ??
          participant?.user_id ??
          participant?.end_user?.id ??
          participant?._end_user?.id ??
          participant?.id;

        return participantId !== null &&
          participantId !== undefined &&
          winnerIds.has(String(participantId));
      });
    }

    if (this.winnersList.length === 0 && this.participantsList.length > 0) {
      this.winnersList = this.participantsList.filter((p: any) =>
        p.is_winner || p.isWinner || p.winner || String(p.status).toLowerCase() === 'winner' || String(p.contestStatus).toLowerCase() === 'winner'
      );
    }

    // If direct winners array contains populated user records not matched in participantsList, map them
    if (this.winnersList.length === 0 && Array.isArray(directContestWinners) && directContestWinners.length > 0) {
      this.winnersList = directContestWinners.map((w: any) => this.normalizeContestParticipant(w));
    }

    this.winnersLoading = false;
  }

  async buildLeaderboard(page: number = 1): Promise<void> {
    if (this.contests_detail?.draw_type !== 'top_points') return;
    this.leaderboardLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEADERBOARD', {
          contest_id: this.contestID,
          page: page - 1,
          per_page: this.leaderboardPerPage
        })
      );
      const items = Array.isArray(response) ? response : (response?.items || response?.data || []);
      this.leaderboardTotal = response?.total ?? response?.itemsReceived ?? items.length;
      this.leaderboardPage = page;
      this.leaderboard = items.map((p: any, i: number) => ({
        ...p,
        _rank: p?.rank ?? ((page - 1) * this.leaderboardPerPage + i + 1),
        _points: Number(p?.points ?? p?.total_points ?? p?.score ?? 0),
        _name: p?.name ?? p?.participant_name ?? this.getParticipantName(p),
        _gender: p?.gender ?? this.getParticipantGender(p)
      }));
    } catch {
      this.leaderboard = [];
    } finally {
      this.leaderboardLoading = false;
    }
  }

  getDrawTypeLabel(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: this.translateService.instant('contestDetails.drawTypeTopPoints'),
      first_to_complete: this.translateService.instant('contestDetails.drawTypeFirstToComplete'),
      random: this.translateService.instant('contestDetails.drawTypeRandom'),
      manual: this.translateService.instant('contestDetails.drawTypeManual'),
    };
    return map[dt] || dt;
  }

  getTieBreakingRule(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: this.translateService.instant('contestDetails.tieBreakTopPoints'),
      first_to_complete: this.translateService.instant('contestDetails.tieBreakFirstToComplete'),
      random: this.translateService.instant('contestDetails.tieBreakRandom'),
      manual: this.translateService.instant('contestDetails.tieBreakManual'),
    };
    return map[dt] || '';
  }

  // Frontend-only label mapping — raw contests_detail.status is unchanged.
  getStatusLabel(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'luqta_pending') {
      return this.translateService.instant('contestDetails.statusPendingApproval');
    }
    const map: Record<string, string> = {
      approved: this.translateService.instant('contestDetails.statusApproved'),
      rejected: this.translateService.instant('contestDetails.statusRejected'),
      draft: this.translateService.instant('contestDetails.statusDraft'),
      completed: this.translateService.instant('contestDetails.statusCompleted'),
    };
    return map[normalized] || (status || 'N/A');
  }

  getStatusBadgeClass(status: string | null | undefined): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'pending' || normalized === 'luqta_pending') return 'submission-status pending';
    if (['approved', 'rejected', 'draft', 'completed'].includes(normalized)) {
      return `submission-status ${normalized}`;
    }
    return 'submission-status default';
  }

  openOverrideModal(): void {
    this.overrideModalVisible = true;
    this.overrideParticipantId = null;
    this.overrideReason = '';
    this.loadAuditLog();
  }

  closeOverrideModal(): void {
    this.overrideModalVisible = false;
  }

  async loadAuditLog(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('WINNER_OVERRIDE_AUDIT_LOG', 'contest_id', this.contestID)
      );
      this.overrideAuditLog = Array.isArray(response) ? response : (response?.data || response?.items || []);
    } catch {
      this.overrideAuditLog = [];
    }
  }

  async submitOverride(): Promise<void> {
    if (!this.overrideParticipantId || !this.overrideReason.trim()) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.selectParticipantAndReason'));
      return;
    }
    this.overrideSubmitting = true;
    try {
      await firstValueFrom(
        this.pagesService.update('WINNER_OVERRIDE', {
          contest_id: this.contestID,
          participant_id: this.overrideParticipantId,
          reason: this.overrideReason.trim()
        })
      );
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.winnerOverriddenSuccess'));
      this.overrideModalVisible = false;
      await this.getContestParticipants(this.contestID);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to override winner.');
    } finally {
      this.overrideSubmitting = false;
    }
  }

  private coerceNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
      if (value === null) return null;
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        return this.coerceNumber(value.value);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'count')) {
        return this.coerceNumber(value.count);
      }
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[%\,]/g, '').trim();
      if (cleaned === '') return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private extractNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const parsed = this.coerceNumber(payload[key]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
    const payloadKeys = Object.keys(payload);
    for (const targetKey of keys) {
      const lowerTarget = targetKey.toLowerCase();
      const matchedKey = payloadKeys.find(k => k.toLowerCase() === lowerTarget);
      if (matchedKey) {
        const parsed = this.coerceNumber(payload[matchedKey]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
    return null;
  }

  private extractString(payload: any, keys: string[]): string | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key];
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized !== '') {
          return normalized;
        }
      }
    }
    const payloadKeys = Object.keys(payload);
    for (const targetKey of keys) {
      const lowerTarget = targetKey.toLowerCase();
      const matchedKey = payloadKeys.find(k => k.toLowerCase() === lowerTarget);
      if (matchedKey) {
        const value = payload[matchedKey];
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized !== '') {
          return normalized;
        }
      }
    }
    return null;
  }

  private extractTrendNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    const payloadKeys = Object.keys(payload);
    for (const key of keys) {
      let raw: any;
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        raw = payload[key];
      } else {
        const lowerKey = key.toLowerCase();
        const matchedKey = payloadKeys.find(k => k.toLowerCase() === lowerKey);
        if (matchedKey) {
          raw = payload[matchedKey];
        } else {
          continue;
        }
      }
      if (raw && typeof raw === 'object') {
        if (Object.prototype.hasOwnProperty.call(raw, 'trend')) {
          const parsedTrend = this.coerceNumber((raw as any).trend);
          if (parsedTrend !== null) {
            return parsedTrend;
          }
        }
        continue;
      }
      const parsed = this.coerceNumber(raw);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private normalizeRate(value: number | null): number | null {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }
    const abs = Math.abs(value);
    if (abs > 100) {
      return value / 100;
    }
    if (abs > 0 && abs <= 1) {
      return value * 100;
    }
    return value;
  }

  private unwrapPerformanceResponse(response: any): any {
    if (!response) return null;
    const candidate = response?.data ?? response?.result ?? response?.body ?? response;
    if (Array.isArray(candidate)) {
      return candidate[0] ?? null;
    }
    if (candidate?.performance) {
      return candidate.performance;
    }
    if (candidate?.contestPerformance) {
      return candidate.contestPerformance;
    }
    if (candidate?.overview) {
      return candidate.overview;
    }
    if (candidate?.stats) {
      return candidate.stats;
    }
    return candidate;
  }

  private resolveTrendClass(label: string | null): string | null {
    if (!label) return null;
    const normalized = label.toLowerCase();
    if (normalized.includes('lower') || normalized.includes('down') || normalized.includes('decrease') || normalized.startsWith('-')) {
      return 'bg-danger';
    }
    if (normalized.includes('higher') || normalized.includes('up') || normalized.includes('increase') || normalized.startsWith('+')) {
      return 'bg-success';
    }
    return 'bg-secondary';
  }

  private buildEmptyPerformance(): ContestPerformance {
    return {
      totalViews: null,
      completedTasks: null,
      completedTasksIsRate: false,
      avgClickRate: null,
      avgCompletionRate: null,
      participationRate: null,
      totalViewsTrend: null,
      completedTasksTrend: null,
      avgClickRateTrend: null,
      participationRateTrend: null,
      totalViewsTrendLabel: null,
      totalViewsTrendClass: null,
      completedTasksTrendLabel: null,
      completedTasksTrendClass: null,
      avgClickRateTrendLabel: null,
      avgClickRateTrendClass: null,
      participationRateTrendLabel: null,
      participationRateTrendClass: null
    };
  }

  private normalizeContestPerformance(response: any): ContestPerformance {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      return this.buildEmptyPerformance();
    }

    const totalViews = this.extractNumber(payload, [
      'total_views', 'totalViews', 'total_view', 'views', 'view', 'total_view_count', 'totalViewCount'
    ]);

    const completedTasks = this.extractNumber(payload, [
      'completed_tasks', 'completedTasks', 'completed', 'completed_count', 'tasks_completed', 'total_completed_tasks'
    ]);

    const avgClickRate = this.extractNumber(payload, [
      'avg_click_rate', 'avgClickRate', 'avg_clicks_rate', 'avg_click_through_rate', 'click_rate', 'clickRate',
      'engagement_rate', 'engagementRate'
    ]);

    const avgCompletionRate = this.extractNumber(payload, [
      'avg_completion_rate', 'avgCompletionRate', 'completion_rate', 'completionRate',
      'average_completion_rate', 'averageCompletionRate'
    ]);

    const participationRate = this.extractNumber(payload, [
      'participation_rate', 'participationRate', 'participation_rate_percent', 'participationPercentage'
    ]);

    const totalViewsTrend = this.extractTrendNumber(payload, [
      'totalViews', 'total_views', 'total_view', 'views', 'view',
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]);
    const completedTasksTrend = this.extractTrendNumber(payload, [
      'completedTasks', 'completed_tasks', 'completed', 'completed_count',
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]);
    const avgClickRateTrend = this.extractTrendNumber(payload, [
      'avgClickRate', 'avg_click_rate', 'averageCTR',
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend', 'averageCTRTrend'
    ]);
    const participationRateTrend = this.extractTrendNumber(payload, [
      'participationRate', 'participation_rate', 'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]);

    const buildTrendLabel = (trend: number | null): string | null => {
      if (trend === null || trend === undefined || Number.isNaN(trend)) return null;
      const direction = trend >= 0 ? 'Higher' : 'Lower';
      return `${Math.abs(trend).toFixed(0)}% ${direction}`;
    };

    const totalViewsTrendLabel = this.extractString(payload, [
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]) ?? buildTrendLabel(totalViewsTrend);
    const completedTasksTrendLabel = this.extractString(payload, [
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]) ?? buildTrendLabel(completedTasksTrend);
    const avgClickRateTrendLabel = this.extractString(payload, [
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend'
    ]) ?? buildTrendLabel(avgClickRateTrend);
    const participationRateTrendLabel = this.extractString(payload, [
      'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]) ?? buildTrendLabel(participationRateTrend);

    const totalViewsTrendClass = totalViewsTrendLabel
      ? this.resolveTrendClass(totalViewsTrendLabel)
      : totalViewsTrend !== null
        ? (totalViewsTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const completedTasksTrendClass = completedTasksTrendLabel
      ? this.resolveTrendClass(completedTasksTrendLabel)
      : completedTasksTrend !== null
        ? (completedTasksTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const avgClickRateTrendClass = avgClickRateTrendLabel
      ? this.resolveTrendClass(avgClickRateTrendLabel)
      : avgClickRateTrend !== null
        ? (avgClickRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const participationRateTrendClass = participationRateTrendLabel
      ? this.resolveTrendClass(participationRateTrendLabel)
      : participationRateTrend !== null
        ? (participationRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;

    const normalizedAvgCompletionRate = this.normalizeRate(avgCompletionRate);
    const normalizedAvgClickRate = this.normalizeRate(avgClickRate);
    const normalizedParticipationRate = this.normalizeRate(participationRate);

    const hasCompletedTasks = completedTasks !== null && completedTasks !== undefined;
    const resolvedCompletedTasks = hasCompletedTasks ? completedTasks : normalizedAvgCompletionRate;
    const completedTasksIsRate = !hasCompletedTasks && normalizedAvgCompletionRate !== null;

    return {
      totalViews,
      completedTasks: resolvedCompletedTasks,
      completedTasksIsRate,
      avgClickRate: normalizedAvgClickRate,
      avgCompletionRate: normalizedAvgCompletionRate,
      participationRate: normalizedParticipationRate,
      totalViewsTrend,
      completedTasksTrend,
      avgClickRateTrend,
      participationRateTrend,
      totalViewsTrendLabel,
      totalViewsTrendClass,
      completedTasksTrendLabel,
      completedTasksTrendClass,
      avgClickRateTrendLabel,
      avgClickRateTrendClass,
      participationRateTrendLabel,
      participationRateTrendClass
    };
  }

  private buildEmptyPerformanceOverview(): OverviewStats {
    return {
      totalContests: 0,
      activeContests: 0,
      pendingContests: 0,
      finishedContests: 0,
      avgClickRate: 0,
      avgCompletionRate: 0,
      avgParticipationRate: 0,
      avgEngagementRate: 0
    };
  }

  private updatePerformanceOverview(response: any): void {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      return;
    }

    const readValue = (obj: any, fallback: number = 0): number => {
      if (obj == null) return fallback;
      const coerced = this.coerceNumber(obj);
      if (coerced !== null && !isNaN(coerced)) return coerced;
      return fallback;
    };
    const readTrend = (obj: any): number | null => {
      if (obj == null) return null;
      if (typeof obj === 'number') return obj;
      if (typeof obj?.trend === 'number') return obj.trend;
      return null;
    };
    const prevFromTrend = (current: number, trend: number | null): number => {
      if (trend == null || !isFinite(trend)) return 0;
      const denom = 1 + trend / 100;
      if (denom === 0) return 0;
      return current / denom;
    };

    const clickKeys = [
      'Clicks', 'clicks', 'totalClicks', 'total_clicks', 'TotalClicks',
      'click', 'total_click', 'totalClick', 'clicks_count', 'click_count',
      'total_clicks_count', 'totalClicksCount', 'totalScans', 'total_scans',
      'scans', 'scan', 'totalScan', 'qr_scans', 'qr_clicks', 'total_qr_scans',
      'scans_count', 'scan_count', 'total_unique_clicks', 'unique_clicks', 'interactions'
    ];
    const viewKeys = [
      'view', 'views', 'totalViews', 'total_views', 'totalView', 'total_view',
      'total_view_count', 'totalViewCount', 'impressions', 'totalImpressions'
    ];
    const participantKeys = [
      'participants', 'totalParticipants', 'total_participants', 'total_participant',
      'totalParticipant', 'participant', 'participants_count', 'participant_count',
      'totalUnique', 'total_unique', 'total_users', 'users', 'user_count',
      'total_joined', 'joins', 'total_joins', 'joined'
    ];
    const completedKeys = [
      'completed', 'completedTasks', 'completed_tasks', 'completed_count',
      'completed_participants', 'total_completed', 'tasks_completed', 'finished',
      'total_finished', 'totalFinished'
    ];

    let totalViews = this.extractNumber(payload, viewKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, viewKeys) : null) ??
      readValue(
        payload?.view,
        readValue(payload?.views, readValue(payload?.totalViews, readValue(payload?.total_views, 0)))
      );
    if (totalViews === 0) {
      const fallbackViews = this.getContestTotalViewsFallback();
      if (fallbackViews !== null && fallbackViews > 0) {
        totalViews = fallbackViews;
      }
    }

    let totalClicks = this.extractNumber(payload, clickKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, clickKeys) : null) ??
      readValue(
        payload?.click,
        readValue(payload?.clicks, readValue(payload?.Clicks, readValue(payload?.totalClicks, readValue(payload?.total_clicks, 0))))
      );
    if (totalClicks === 0) {
      const fallbackClicks = this.getContestTotalClicksFallback();
      if (fallbackClicks !== null && fallbackClicks > 0) {
        totalClicks = fallbackClicks;
      }
    }

    let participants = this.extractNumber(payload, participantKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, participantKeys) : null) ??
      readValue(
        payload?.participants,
        readValue(payload?.totalParticipants, readValue(payload?.total_participants, 0))
      );
    if (participants === 0) {
      const fallbackParticipants = this.getUniqueParticipantsCount();
      if (fallbackParticipants !== null && fallbackParticipants > 0) {
        participants = fallbackParticipants;
      }
    }

    const completed = this.extractNumber(payload, completedKeys) ??
      (typeof response === 'object' ? this.extractNumber(response, completedKeys) : null) ??
      readValue(
        payload?.completed,
        readValue(payload?.completedTasks, readValue(payload?.completed_tasks, 0))
      );

    const overallSuccessRateRaw = readValue(
      payload?.overallSuccessRate,
      readValue(payload?.overall_success_rate, readValue(payload?.averageClickThroughRate, readValue(payload?.averageCTR, 0)))
    );
    const avgCompletionRaw = readValue(
      payload?.completionRate,
      readValue(payload?.avgCompletionRate, readValue(payload?.averageCompletionRate, readValue(payload?.avg_completion_rate, 0)))
    );
    const avgParticipationRaw = readValue(
      payload?.participationRate,
      readValue(payload?.avgParticipationRate, readValue(payload?.averageParticipationRate, readValue(payload?.avg_participation_rate, 0)))
    );
    const avgEngagementRaw = readValue(
      payload?.engagementRate,
      readValue(payload?.avgEngagementRate, readValue(payload?.averageEngagementRate, readValue(payload?.avg_engagement_rate, 0)))
    );

    let overallSuccessRate = this.normalizeRate(overallSuccessRateRaw) ?? 0;
    if (overallSuccessRate === 0 && totalViews > 0 && totalClicks > 0) {
      overallSuccessRate = this.normalizeRate((totalClicks / totalViews) * 100) ?? 0;
    }
    const avgCompletion = this.normalizeRate(avgCompletionRaw) ?? 0;
    let avgParticipation = this.normalizeRate(avgParticipationRaw) ?? 0;
    if (avgParticipation === 0 && totalViews > 0 && participants > 0) {
      avgParticipation = this.normalizeRate((participants / totalViews) * 100) ?? 0;
    }
    const avgEngagement = this.normalizeRate(avgEngagementRaw) ?? 0;

    const totalTrend = this.extractTrendNumber(payload, viewKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, viewKeys) : null) ??
      readTrend(payload?.view) ?? readTrend(payload?.views) ?? readTrend(payload?.totalViews);
    const activeTrend = this.extractTrendNumber(payload, clickKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, clickKeys) : null) ??
      readTrend(payload?.click) ?? readTrend(payload?.clicks) ?? readTrend(payload?.Clicks) ?? readTrend(payload?.totalClicks);
    const pendingTrend = this.extractTrendNumber(payload, participantKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, participantKeys) : null) ??
      readTrend(payload?.participants) ?? readTrend(payload?.totalParticipants);
    const finishedTrend = this.extractTrendNumber(payload, completedKeys) ??
      (typeof response === 'object' ? this.extractTrendNumber(response, completedKeys) : null) ??
      readTrend(payload?.completed) ?? readTrend(payload?.completedTasks);
    const overallTrend = readTrend(payload?.overallSuccessRate) ?? readTrend(payload?.averageClickThroughRate);
    const completionTrend = readTrend(payload?.completionRate) ?? readTrend(payload?.avgCompletionRate) ?? readTrend(payload?.averageCompletionRate);
    const participationTrend = readTrend(payload?.participationRate) ?? readTrend(payload?.avgParticipationRate) ?? readTrend(payload?.averageParticipationRate);
    const engagementTrend = readTrend(payload?.engagementRate) ?? readTrend(payload?.avgEngagementRate) ?? readTrend(payload?.averageEngagementRate);

    this.performanceStats = {
      totalContests: totalViews,
      activeContests: totalClicks,
      pendingContests: participants,
      finishedContests: completed,
      avgClickRate: overallSuccessRate,
      avgCompletionRate: avgCompletion,
      avgParticipationRate: avgParticipation,
      avgEngagementRate: avgEngagement
    };

    this.performancePreviousStats = {
      totalContests: prevFromTrend(totalViews, totalTrend),
      activeContests: prevFromTrend(totalClicks, activeTrend),
      pendingContests: prevFromTrend(participants, pendingTrend),
      finishedContests: prevFromTrend(completed, finishedTrend),
      avgClickRate: prevFromTrend(overallSuccessRate, overallTrend),
      avgCompletionRate: prevFromTrend(avgCompletion, completionTrend),
      avgParticipationRate: prevFromTrend(avgParticipation, participationTrend),
      avgEngagementRate: prevFromTrend(avgEngagement, engagementTrend)
    };

    this.performanceTrends = {
      totalContests: totalTrend ?? 0,
      activeContests: activeTrend ?? 0,
      pendingContests: pendingTrend ?? 0,
      finishedContests: finishedTrend ?? 0,
      avgClickRate: overallTrend ?? 0,
      avgCompletionRate: completionTrend ?? 0,
      avgParticipationRate: participationTrend ?? 0,
      avgEngagementRate: engagementTrend ?? 0,
      overallSuccessRate: overallTrend ?? 0
    };
  }

  async getContestPerformance(contestId: number | string): Promise<void> {
    this.performanceLoading = true;
    this.performanceError = null;

    try {
      const parsedContestId = Number(contestId);
      if (!Number.isFinite(parsedContestId)) {
        throw new Error('Invalid contest id for performance request.');
      }
      const startDate = this.performanceDateRange?.[0] ?? null;
      const endDate = this.performanceDateRange?.[1] ?? null;
      const params = {
        start_date: startDate ? Math.floor(startDate.getTime() / 1000) : '',
        end_date: endDate ? Math.floor(endDate.getTime() / 1000) : '',
        contest_id: parsedContestId
      };
      const response = await firstValueFrom(
        this.pagesService.getAllById('CONTEST_DETAIL_OVERVIEW', params)
      );
      this.contestPerformance = this.normalizeContestPerformance(response);
      this.updatePerformanceOverview(response);
    } catch (error: any) {
      this.contestPerformance = this.buildEmptyPerformance();
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      this.performanceError = 'Performance data unavailable.';
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest performance:', error);
      }
    } finally {
      this.performanceLoading = false;
      this.applyPerformanceFallbacks();
    }
  }

  private getUniqueParticipantsCount(): number | null {
    if (!Array.isArray(this.participantsList) || this.participantsList.length === 0) {
      return null;
    }

    const participantIds = new Set<string>();
    this.participantsList.forEach((p: any) => {
      const id = p?.participant_id ?? p?.user_id ?? p?._end_user?.id ?? p?.id;
      if (id !== null && id !== undefined) {
        participantIds.add(String(id));
      }
    });

    return participantIds.size > 0 ? participantIds.size : this.participantsList.length;
  }

  private getContestTotalViewsFallback(): number | null {
    return this.extractNumber(this.contests_detail, [
      'total_views', 'totalViews', 'views', 'view', 'total_view_count', 'total_view', 'impressions'
    ]);
  }

  private getContestTotalClicksFallback(): number | null {
    const clickKeys = [
      'Clicks', 'clicks', 'total_clicks', 'totalClicks', 'TotalClicks',
      'click', 'total_click', 'totalClick', 'clicks_count', 'click_count',
      'total_clicks_count', 'totalClicksCount', 'totalScans', 'total_scans',
      'scans', 'scan', 'totalScan', 'qr_scans', 'qr_clicks', 'total_qr_scans',
      'scans_count', 'scan_count', 'total_unique_clicks', 'unique_clicks', 'interactions'
    ];

    // 1. From contests_detail (from GET_SINGLE_CONTEST which queries x2_dev_18_contest)
    const fromDetail = this.extractNumber(this.contests_detail, clickKeys);
    if (fromDetail !== null && fromDetail > 0) {
      return fromDetail;
    }

    // 2. From analyticsActivity (from QR_REPORT_DETAIL / ANALYSIS_URL_Reports_DETAIL)
    if (Array.isArray(this.analyticsActivity) && this.analyticsActivity.length > 0) {
      const fromAnalytics = this.analyticsActivity.reduce((acc: number, u: any) => {
        const itemClicks = this.extractNumber(u, clickKeys);
        if (itemClicks !== null && itemClicks > 0) return acc + itemClicks;
        const levelSteps = (Number(u?.levelsCompleted || 0) + Number(u?.levelsInProgress || 0));
        return acc + (levelSteps > 0 ? levelSteps : (u?.contestId || u?.id ? 1 : 0));
      }, 0);
      if (fromAnalytics > 0) {
        return fromAnalytics;
      }
    }

    // 3. From participants: each participant who participated clicked/scanned to participate
    const uniqueParticipants = this.getUniqueParticipantsCount();
    if (uniqueParticipants !== null && uniqueParticipants > 0) {
      return uniqueParticipants;
    }

    // 4. Fallback to total views if clicks were unrecorded but views exist
    const views = this.performanceStats?.totalContests || this.getContestTotalViewsFallback();
    if (views && views > 0 && (!this.performanceStats?.activeContests || this.performanceStats.activeContests === 0)) {
      return views;
    }

    return null;
  }

  private applyPerformanceFallbacks(): void {
    const nextPerformance: ContestPerformance = { ...this.contestPerformance };
    const fallbackViews = this.getContestTotalViewsFallback();
    const fallbackParticipants = this.getUniqueParticipantsCount();
    const fallbackClicks = this.getContestTotalClicksFallback();

    if ((nextPerformance.totalViews === null || nextPerformance.totalViews === 0) && fallbackViews && fallbackViews > 0) {
      nextPerformance.totalViews = fallbackViews;
    }

    if ((nextPerformance.participationRate === null || nextPerformance.participationRate === 0) &&
      fallbackViews && fallbackViews > 0 &&
      fallbackParticipants && fallbackParticipants > 0) {
      nextPerformance.participationRate = this.normalizeRate((fallbackParticipants / fallbackViews) * 100);
    }

    this.contestPerformance = nextPerformance;

    // Update performanceStats which drives the UI cards
    const stats = { ...this.performanceStats };
    const prevStats = { ...this.performancePreviousStats };
    const trends = { ...this.performanceTrends };
    let updated = false;

    if ((!stats.totalContests || stats.totalContests === 0) && fallbackViews && fallbackViews > 0) {
      stats.totalContests = fallbackViews;
      prevStats.totalContests = prevStats.totalContests || fallbackViews;
      updated = true;
    }

    if ((!stats.pendingContests || stats.pendingContests === 0) && fallbackParticipants && fallbackParticipants > 0) {
      stats.pendingContests = fallbackParticipants;
      prevStats.pendingContests = prevStats.pendingContests || fallbackParticipants;
      updated = true;
    }

    if ((!stats.activeContests || stats.activeContests === 0) && fallbackClicks && fallbackClicks > 0) {
      stats.activeContests = fallbackClicks;
      if ((!trends.activeContests || trends.activeContests === 0) && trends.pendingContests) {
        trends.activeContests = trends.pendingContests;
      }
      const activeTrend = trends.activeContests ?? 0;
      const denom = 1 + activeTrend / 100;
      prevStats.activeContests = denom !== 0 ? fallbackClicks / denom : fallbackClicks;
      updated = true;
    }

    const views = stats.totalContests;
    const participants = stats.pendingContests;
    const clicks = stats.activeContests;

    if ((!stats.avgParticipationRate || stats.avgParticipationRate === 0) && views > 0 && participants > 0) {
      stats.avgParticipationRate = this.normalizeRate((participants / views) * 100) ?? 0;
      updated = true;
    }

    if ((!stats.avgClickRate || stats.avgClickRate === 0) && views > 0 && clicks > 0) {
      stats.avgClickRate = this.normalizeRate((clicks / views) * 100) ?? 0;
      updated = true;
    }

    if (updated) {
      this.performanceStats = stats;
      this.performancePreviousStats = prevStats;
      this.performanceTrends = trends;
    }
  }

  formatMetric(value: number | null, isPercent: boolean = false, fractionDigits: number = 1): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }
    if (isPercent) {
      return `${value.toFixed(fractionDigits)}%`;
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  getPercentWidth(value: number | null): number {
    if (value === null || value === undefined || Number.isNaN(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  formatDateToString(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  onPerformanceDateChange(dates: Date[]): void {
    this.performanceDateRange = dates || [];
    if (this.contestID) {
      this.getContestPerformance(this.contestID);
    }
  }

  getTrendBadge(trend: number | null | undefined): { value: number; isPositive: boolean } {
    const safe = typeof trend === 'number' && isFinite(trend) ? trend : 0;
    return { value: Math.abs(safe), isPositive: safe >= 0 };
  }

  getPercentageChange(current: number, previous: number): { value: number; isPositive: boolean } {
    if (previous === 0) {
      return { value: 0, isPositive: true };
    }
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0
    };
  }
  getTimeRemaining(createdAt: string) {
    return this.sharedService.timeRemaining(createdAt);
  }
  // Get variables to display (limited or all)
  getDisplayedVariables(level: any): any[] {
    if (!level.client_variables || level.client_variables.length === 0) {
      return [];
    }
    const showAll = this.showAllVariables[level.id];
    return showAll ? level.client_variables : level.client_variables.slice(0, this.maxVariablesToShow);
  }
  shouldShowMore(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      !this.showAllVariables[level.id];
  }
  shouldShowLess(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      this.showAllVariables[level.id];
  }
  LevelUID(uid: string): void {
    if (uid) {
      this.clipboard.copy(uid);
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelUidCopiedSuccess'));
    } else {
      this.sharedService.createNotification('warning', this.translateService.instant('contestDetails.noUidForLevel'));
    }
  }
  getRemainingVariablesCount(level: any): number {
    return level.client_variables ? level.client_variables.length - this.maxVariablesToShow : 0;
  }
  toggleVariablesDisplay(levelId: string): void {
    this.showAllVariables[levelId] = !this.showAllVariables[levelId];
  }
  private formatDate(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    const date = new Date(Number(timestamp));
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private resolveDrawDate(contestData: any): string {
    const drawList = Array.isArray(contestData?.contest_draw) ? contestData.contest_draw : [];
    if (drawList.length > 0) {
      const latestDraw = [...drawList].sort(
        (a: any, b: any) => Number(b?.created_at || 0) - Number(a?.created_at || 0)
      )[0];
      if (latestDraw?.created_at) {
        return this.formatDate(latestDraw.created_at);
      }
    }

    return this.formatDate(contestData?.prize_announce_date);
  }

  copyUID(value: string): void {
    if (value) {
      this.clipboard.copy(value);
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.copiedToClipboard'));
    } else {
      this.sharedService.createNotification('warning', this.translateService.instant('contestDetails.nothingToCopy'));
    }
  }

  copyLevelId(levelId: number): void {
    if (levelId) {
      this.clipboard.copy(levelId.toString());
      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelIdCopied'));
    }
  }

  // Helper method to check if value exists
  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  // Toggle edit mode for a level
  onQuizDropdownOpen(isOpen: boolean): void {
    if (isOpen && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }
  }

  getQuizzesRecords(): void {
    if (this.quizzesLoaded) return;
    this.authService.getAll('GET_QUIZZES_LOOKUP').subscribe({
      next: (response: any) => {
        this.quizzesList = response || [];
        this.quizzesLoaded = true;
      },
      error: () => {
        this.sharedService.createNotification('error', this.translateService.instant('contestDetails.failedToLoadQuizzes'));
      },
    });
  }

  getQuizTitle(quizId: number): string {
    const quiz = this.quizzesList.find(q => q.id === quizId);
    return quiz?.quiz_title || 'Quiz #' + quizId;
  }

  toggleEditLevel(levelId: string, level: any): void {
    if (this.editingLevels[levelId]) {
      // Cancel editing
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } else {
      // Start editing - create form for this level
      this.editingLevels[levelId] = true;
      this.levelForms[levelId] = this.createLevelForm(level);
    }
  }

  // Create form for editing level
  createLevelForm(level: any): FormGroup {
    const levelType = level.level_type || level.type;
    const isTextLevel = levelType === 'text';

    const form = this.fb.group({
      name: [level.name || '', Validators.required],
      arabic_name: [level.arabic_name || ''],
      description: [level.description || '', Validators.required],
      arabic_description: [level.arabic_description || '', Validators.required],
      textContent: this.fb.array(
        level.textContent && level.textContent.trim() !== ''
          ? this.splitTextContent(level.textContent).map((text: string) =>
            this.fb.control(text, isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])
          )
          : [this.fb.control('', isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])]
      ),
      qrData: [level.qrData || level.textContent || ''],
      client_webhook_url: [level.client_webhook_url || ''],
      imageFile: [null],
      client_variables: this.fb.array([]),
      quizzes_id: [level.quizzes_id || null],
      min_answers_required: [
        level.min_answers_required || 1,
        levelType === 'survey'
          ? [Validators.required, Validators.min(1), Validators.max(level.survey_questions?.length || 1)]
          : [Validators.min(1)]
      ],
      question_order: [level.question_order || 'fixed'],
      link_task_instruction: [level.link_task_instruction || ''],
      link_task_url: [level.link_task_url || ''],
      link_task_timer: [level.link_task_timer || 0],
    });

    // Set existing image preview if available
    if (level.image && level.image.url) {
      this.levelImagePreviews[level.id] = level.image.url;
    }

    // Pre-load quizzes if level type is quiz
    if ((level.level_type || level.type) === 'quiz' && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }

    // Add existing client variables if available
    if (level.client_variables && level.client_variables.length > 0) {
      const variablesArray = form.get('client_variables') as FormArray;
      level.client_variables.forEach((variable: any) => {
        variablesArray.push(this.fb.group({
          variable_name: [variable.variable_name || '', Validators.required],
          display_name: [variable.display_name || '', Validators.required],
          datatype: [variable.datatype || 'string', Validators.required],
        }));
      });
    }

    return form;
  }

  // Save updated level
  async saveLevel(levelId: string, level: any): Promise<void> {
    const form = this.levelForms[levelId];

    if (!form || form.invalid) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.fillRequiredFields'));
      form?.markAllAsTouched();
      return;
    }

    this.sharedService.showLoader();

    try {
      const formValue = form.value;
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: formValue.name,
        arabic_name: formValue.arabic_name ?? level.arabic_name ?? '',
        description: formValue.description,
        arabic_description: formValue.arabic_description,
        type: levelType,
        start_date: level.level_start_date ?? level.start_date ?? null,
        level_start_date: level.level_start_date ?? level.start_date ?? null,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
      };

      // Add type-specific fields
      if (levelType === 'text') {
        const textContentArray = formValue.textContent || [];
        updatePayload.textContent = textContentArray.filter((text: string) => text.trim() !== '').join(' || ');
      } else if (levelType === 'qr') {
        updatePayload.qrData = formValue.qrData;
        updatePayload.textContent = formValue.qrData;
      } else if (levelType === 'image') {
        // Handle image upload - check preview first (has base64), then file
        const previewImage = this.levelImagePreviews[levelId];
        const imageFile = formValue.imageFile;

        if (previewImage && typeof previewImage === 'string' && previewImage.startsWith('data:image')) {
          // Use base64 from preview (newly uploaded image)
          updatePayload.image = { path: previewImage };
        } else if (imageFile && imageFile instanceof File) {
          // Convert file to base64 if preview not available
          const imageBase64 = await this.fileToBase64(imageFile);
          updatePayload.image = { path: imageBase64 };
        } else if (level.image) {
          // Keep existing image from server
          updatePayload.image = level.image;
        }
      } else if (levelType === 'client_webhook') {
        updatePayload.client_webhook_url = formValue.client_webhook_url || '';
        updatePayload.client_variables = formValue.client_variables || [];
      } else if (levelType === 'quiz') {
        updatePayload.quizzes_id = formValue.quizzes_id || null;
      } else if (levelType === 'survey') {
        updatePayload.min_answers_required = Number(formValue.min_answers_required) || 1;
        updatePayload.question_order = formValue.question_order || 'fixed';
        updatePayload.survey_questions = level.survey_questions || [];
      } else if (levelType === 'link_task') {
        updatePayload.link_task_instruction = formValue.link_task_instruction || '';
        updatePayload.link_task_url = formValue.link_task_url || '';
        updatePayload.link_task_timer = Number(formValue.link_task_timer) || 0;
      } else {
        // For other types, preserve existing values
        updatePayload.client_webhook_url = '';
        updatePayload.client_variables = [];
      }

      // Update level via API — preserve ALL levels so other levels (e.g. QR) and dates are never lost
      const allLevelsPayload = this.levels_of_contest.map((lvl: any, index: number) => {
        if (lvl.id === level.id) {
          return {
            ...updatePayload,
            order: lvl.order ?? (index + 1),
          };
        }
        return this.formatLevelForApi(lvl, index);
      });

      const levelData = {
        level_details: allLevelsPayload,
        contest_id: this.contestID,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local data
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        const textContentArray = formValue.textContent || [];
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          name: formValue.name,
          arabic_name: formValue.arabic_name ?? level.arabic_name ?? '',
          description: formValue.description,
          arabic_description: formValue.arabic_description,
          textContent: textContentArray.filter((text: string) => text.trim() !== '').join(' || '),
          qrData: formValue.qrData,
          client_webhook_url: formValue.client_webhook_url,
          client_variables: formValue.client_variables || [],
          quizzes_id: formValue.quizzes_id,
          min_answers_required: formValue.min_answers_required,
          question_order: formValue.question_order,
          link_task_instruction: formValue.link_task_instruction,
          link_task_url: formValue.link_task_url,
          link_task_timer: formValue.link_task_timer,
        };
      }

      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.levelUpdatedSuccess'));
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to update level');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Format existing level object for API submission preserving all configuration
  formatLevelForApi(lvl: any, index?: number): any {
    const levelRecord = lvl?.level_details ?? lvl?.level_detail ?? lvl?.details ?? lvl;
    const levelType = lvl?.level_type || lvl?.type || levelRecord?.level_type || levelRecord?.type || '';
    const startDate = lvl?.level_start_date ?? lvl?.start_date ?? levelRecord?.level_start_date ?? levelRecord?.start_date ?? null;
    const timeline = lvl?.level_timeline ?? lvl?.timeline ?? levelRecord?.level_timeline ?? levelRecord?.timeline ?? null;
    const order = lvl?.order ?? levelRecord?.order ?? (index !== undefined ? index + 1 : undefined);

    const payload: any = {
      id: lvl?.id || levelRecord?.id || null,
      name: lvl?.name ?? levelRecord?.name ?? '',
      arabic_name: lvl?.arabic_name ?? levelRecord?.arabic_name ?? '',
      description: lvl?.description ?? levelRecord?.description ?? '',
      arabic_description: lvl?.arabic_description ?? levelRecord?.arabic_description ?? '',
      type: levelType,
      start_date: startDate,
      level_start_date: startDate,
      level_timeline: timeline,
      luqta_webhook_url: lvl?.luqta_webhook_url ?? levelRecord?.luqta_webhook_url ?? '',
      client_webhook_url: lvl?.client_webhook_url ?? levelRecord?.client_webhook_url ?? '',
      client_variables: lvl?.client_variables ?? levelRecord?.client_variables ?? [],
      order: order,
    };

    if (levelType === 'text') {
      payload.textContent = lvl?.textContent ?? levelRecord?.textContent ?? '';
      payload.arabic_textContent = lvl?.arabic_textContent ?? levelRecord?.arabic_textContent ?? '';
    } else if (levelType === 'qr') {
      const qr = lvl?.qrData ?? levelRecord?.qrData ?? lvl?.qr_data ?? levelRecord?.qr_data ?? lvl?.textContent ?? levelRecord?.textContent ?? '';
      payload.qrData = qr;
      payload.textContent = qr;
    } else if (levelType === 'image') {
      payload.image = lvl?.image ?? levelRecord?.image ?? null;
    } else if (levelType === 'quiz') {
      payload.quizzes_id = lvl?.quizzes_id ?? levelRecord?.quizzes_id ?? null;
      payload.quiz_text_en = lvl?.quiz_text_en ?? levelRecord?.quiz_text_en ?? '';
      payload.quiz_text_ar = lvl?.quiz_text_ar ?? levelRecord?.quiz_text_ar ?? '';
    } else if (levelType === 'survey') {
      payload.survey_questions = lvl?.survey_questions ?? levelRecord?.survey_questions ?? [];
      payload.min_answers_required = lvl?.min_answers_required ?? levelRecord?.min_answers_required ?? 1;
      payload.question_order = lvl?.question_order ?? levelRecord?.question_order ?? 'fixed';
    } else if (levelType === 'link_task') {
      payload.link_task_instruction = lvl?.link_task_instruction ?? levelRecord?.link_task_instruction ?? '';
      payload.link_task_url = lvl?.link_task_url ?? levelRecord?.link_task_url ?? '';
      payload.link_task_timer = lvl?.link_task_timer ?? levelRecord?.link_task_timer ?? 30;
    }

    return payload;
  }

  // Check if level is in edit mode
  isEditingLevel(levelId: string): boolean {
    return this.editingLevels[levelId] || false;
  }

  // Get form for a level
  getLevelForm(levelId: string): FormGroup | null {
    return this.levelForms[levelId] || null;
  }

  // Get textContent FormArray for a specific level
  getTextContents(levelId: string): FormArray {
    const levelForm = this.getLevelForm(levelId);
    return (levelForm?.get('textContent') as FormArray) ?? this.fb.array([]);
  }

  // Add a new text content field
  addTextContent(levelId: string): void {
    const textContents = this.getTextContents(levelId);
    textContents.push(this.fb.control('', [Validators.required, Validators.maxLength(1000)]));
  }

  // Remove a text content field
  removeTextContent(levelId: string, textIndex: number): void {
    const textContents = this.getTextContents(levelId);
    if (textContents && textContents.length > 1) {
      textContents.removeAt(textIndex);
    }
  }

  // API responses have been observed returning level fields under different
  // shapes/names (e.g. nested level_details, text_content instead of textContent,
  // type instead of level_type) — normalize so display bindings don't silently
  // fail to find the answer data.
  private normalizeLevelForDisplay(level: any): any {
    const levelRecord = level?.level_details ?? level?.level_detail ?? level?.details ?? level;
    const level_type =
      level?.level_type ||
      levelRecord?.level_type ||
      level?.type ||
      levelRecord?.type ||
      level?.levelType ||
      levelRecord?.levelType ||
      '';
    const textContent =
      level?.textContent ??
      levelRecord?.textContent ??
      level?.text_content ??
      levelRecord?.text_content ??
      level?.text ??
      levelRecord?.text ??
      '';

    // For QR levels, ensure qrData is always populated — the API may only
    // return it under textContent, so fall back to that.
    const qrData =
      level?.qrData ??
      levelRecord?.qrData ??
      level?.qr_data ??
      levelRecord?.qr_data ??
      textContent;

    const rawSurveyQuestions = level?.survey_questions ?? levelRecord?.survey_questions ?? [];
    const survey_questions = (Array.isArray(rawSurveyQuestions) ? rawSurveyQuestions : []).map((q: any) => {
      const rawOptions = q.options ?? q.answer_options ?? q.choices ?? [];
      const options = Array.isArray(rawOptions) ? rawOptions : [];
      const hasOptions = options.length > 0;
      const question_type = hasOptions ? 'mcq' : (q.question_type || q.type || 'open_text');
      return {
        ...q,
        options,
        question_type
      };
    });

    const arabic_name =
      level?.arabic_name ??
      levelRecord?.arabic_name ??
      '';

    const level_start_date =
      level?.level_start_date ??
      levelRecord?.level_start_date ??
      level?.start_date ??
      levelRecord?.start_date ??
      level?.level_start ??
      levelRecord?.level_start ??
      null;

    const level_timeline =
      level?.level_timeline ??
      levelRecord?.level_timeline ??
      level?.timeline ??
      levelRecord?.timeline ??
      null;

    const order =
      level?.order ??
      levelRecord?.order ??
      level?.level_order ??
      levelRecord?.level_order ??
      undefined;

    return {
      ...level,
      level_type,
      textContent,
      qrData,
      survey_questions,
      arabic_name: arabic_name || level.arabic_name || '',
      level_start_date,
      start_date: level_start_date,
      level_timeline,
      order
    };
  }

  // Split textContent by || for display
  splitTextContent(textContent: string): string[] {
    if (!textContent || textContent.trim() === '') {
      return [];
    }
    return textContent
      .split(/\s*\|\|\s*/)
      .map((text) => text.trim())
      .filter((text) => text !== '');
  }

  // Get edit button text based on level type
  getEditButtonText(level: any): string {
    if (!level) return this.translateService.instant('contestDetails.editLevel');

    const levelType = level.level_type || level.type;

    switch (levelType) {
      case 'text':
        return this.translateService.instant('contestDetails.editTextLevel');
      case 'qr':
        return this.translateService.instant('contestDetails.editQrLevel');
      case 'image':
        return this.translateService.instant('contestDetails.editImageLevel');
      case 'luqta_weebhook':
        return this.translateService.instant('contestDetails.editLuqtaWebhook');
      case 'client_webhook':
        return this.translateService.instant('contestDetails.editClientWebhook');
      case 'client_qr_completion':
        return 'Client QR Completion';
      default:
        return this.translateService.instant('contestDetails.editLevel');
    }
  }

  // Trigger level image input
  triggerLevelImageInput(levelId: string): void {
    const fileInput = document.getElementById(`imageFile_${levelId}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle level image selection
  onLevelImageSelected(event: Event, levelId: string): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.levelImagePreviews[levelId] = reader.result;
        this.levelImageLoadErrors[levelId] = false;
        const form = this.levelForms[levelId];
        if (form) {
          form.patchValue({ imageFile: file });
        }
        // Show modal after image is uploaded
        this.showImageModal(levelId);
      };
      reader.readAsDataURL(file);
    }
  }

  // Show image preview modal
  showImageModal(levelId: string): void {
    this.isImageModalVisible[levelId] = true;
  }

  // Handle image modal OK
  handleImageOk(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Handle image modal cancel
  handleImageCancel(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Download level image
  async downloadLevelImage(levelId: any, level?: any): Promise<void> {
    const rawUrl = this.levelImagePreviews[levelId] ||
      level?.image?.url ||
      level?.image?.path ||
      (typeof level?.image === 'string' ? level.image : null);

    if (!rawUrl || typeof rawUrl !== 'string') {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.uploadImageFirst') || 'No image available to download.');
      return;
    }

    const levelName = level?.name || `level_${levelId}`;
    const cleanFileName = `${(levelName.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image')}_${Date.now()}.png`;

    const success = await this.sharedService.downloadImageFile(rawUrl, cleanFileName);
    if (!success) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.downloadError') || 'Failed to download image.');
    }
  }

  // Save image and keep preview visible
  async saveImageAndShowPreview(levelId: string, level: any): Promise<void> {
    const previewImage = this.levelImagePreviews[levelId];

    if (!previewImage || typeof previewImage !== 'string' || !previewImage.startsWith('data:image')) {
      this.sharedService.createNotification('error', this.translateService.instant('contestDetails.uploadImageFirst'));
      return;
    }

    this.sharedService.showLoader();

    try {
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: level.name,
        description: level.description,
        type: levelType,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
        image: { path: previewImage }
      };

      const allLevelsPayload = this.levels_of_contest.map((lvl: any, index: number) => {
        if (lvl.id === level.id) {
          return {
            ...updatePayload,
            start_date: lvl.level_start_date ?? lvl.start_date ?? null,
            level_start_date: lvl.level_start_date ?? lvl.start_date ?? null,
            order: lvl.order ?? (index + 1),
          };
        }
        return this.formatLevelForApi(lvl, index);
      });

      const levelData = {
        level_details: allLevelsPayload,
        contest_id: this.contestID,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local level data immediately with preview image
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          image: { url: previewImage, path: previewImage }
        };
      }

      this.sharedService.createNotification('success', this.translateService.instant('contestDetails.imageSavedSuccess'));

      // Refresh contest details from server to get updated data
      await this.getContestDetails(this.contestID);

    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to save image');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Convert file to base64
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Get client variables FormArray for a level
  getClientVariables(levelId: string): FormArray {
    const form = this.levelForms[levelId];
    return form?.get('client_variables') as FormArray;
  }

  // Add new variable to client webhook
  addVariable(levelId: string): void {
    const variables = this.getClientVariables(levelId);
    if (variables) {
      variables.push(this.fb.group({
        variable_name: ['', Validators.required],
        display_name: ['', Validators.required],
        datatype: ['string', Validators.required],
      }));
    }
  }

  // Remove variable from client webhook
  removeVariable(levelId: string, index: number): void {
    const variables = this.getClientVariables(levelId);
    if (variables && index >= 0 && index < variables.length) {
      variables.removeAt(index);
    }
  }

  // Helper method to get countries as array from location string
  getCountriesArray(location: string | null | undefined): string[] {
    if (!location || location.trim() === '') {
      return [];
    }
    // Split by comma, semicolon, or pipe and trim whitespace
    const countries = location.split(/[,;|]/).map(c => c.trim()).filter(c => c !== '');
    return countries;
  }

  // Search handler for participants
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  // Open filter modal for participants
  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  // Handle filters applied from modal
  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    this.applyClientFilters();
  }

  // Handle filter reset
  onFiltersReset(): void {
    this.currentFilters = {};
    this.applyClientFilters();
  }

  private getGenderFilterValues(selectedGender: string | null | undefined): string[] {
    const gender = String(selectedGender || '').trim().toLowerCase();

    if (gender === 'male') {
      return ['male', '\u0630\u0643\u0631'];
    }

    if (gender === 'female') {
      return ['female', '\u0623\u0646\u062b\u0649'];
    }

    if (gender === 'unspecified') {
      return [''];
    }

    if (gender === 'all') {
      return ['male', '\u0630\u0643\u0631', 'female', '\u0623\u0646\u062b\u0649', ''];
    }

    return [];
  }

  // Check if any filters are active
  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }

  // Clear all filters
  clearAllFilters(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        dateRange: null, level: null, status: null, format: null,
        type: null, viewType: null, location: [], gender: null,
        contest: null, age: null, completionRate: null,
        participant_name: null, participant_email: null
      });
    }
    this.applyClientFilters();
  }

  // Apply client-side filtering on participants
  applyClientFilters(): void {
    let filtered = [...this.participantsList];
    const search = this.searchTerm?.trim().toLowerCase();
    const filters = this.currentFilters;

    // Search filter
    if (search) {
      filtered = filtered.filter(p => {
        const name = this.getParticipantName(p).toLowerCase();
        const gender = this.getParticipantGender(p).toLowerCase();
        const location = this.getParticipantLocation(p).toLowerCase();
        return name.includes(search) || gender.includes(search) || location.includes(search);
      });
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(
        p => String(p.status || p.participant_status || '').toLowerCase() === String(filters.status).toLowerCase()
      );
    }

    // Gender filter
    if (filters.gender) {
      const allowedGenderValues = this.getGenderFilterValues(filters.gender).map((value) =>
        value.toLowerCase().trim()
      );
      filtered = filtered.filter(p => {
        const participantGender = this.getParticipantGender(p);
        const normalizedGender = participantGender === 'N/A'
          ? ''
          : String(participantGender || '').toLowerCase().trim();
        return allowedGenderValues.includes(normalizedGender);
      });
    }

    // Location filter
    if (filters.location && Array.isArray(filters.location) && filters.location.length > 0) {
      filtered = filtered.filter(p => {
        const loc = this.getParticipantLocation(p).toLowerCase();
        return filters.location.some((l: string) => loc.includes(l.toLowerCase()));
      });
    }

    this.filteredParticipants = filtered;
  }

  // Open drawer to view participant's level progression
  openLevelsDrawer(participant: any): void {
    this.selectedParticipant = participant;
    // Get levels from the contest's levels_of_contest and map participant's progress
    this.selectedParticipantLevels = this.levels_of_contest.map((level: any, index: number) => {
      // Check if participant has completed this level
      const participantLevel = participant.levels_detail?.find((pl: any) => (pl.level_id === level.id || pl.id === level.id)) ||
        participant.levelsDetail?.find((pl: any) => (pl.level_id === level.id || pl.id === level.id));

      const userId = participant?._end_user?.id ?? participant?.participant_id;
      const progressFromLevel = (level.users_progress || []).find((up: any) => up.user_id === userId);

      const userAnswer = participantLevel?.user_answer ?? participantLevel?.answer ?? participantLevel?.submitted_answer ?? participantLevel?.text_answer ?? progressFromLevel?.user_answer ?? progressFromLevel?.answer ?? null;

      return {
        name: level.name || `Level ${index + 1}`,
        status: participantLevel?.status || progressFromLevel?.status || ((participant.status || participant.participant_status) === 'completed' ? 'completed' : 'pending'),
        points: participantLevel?.points || progressFromLevel?.points || 0,
        completed_at: participantLevel?.completed_at || progressFromLevel?.completed_at || null,
        level_type: level.level_type || level.type,
        accept_any_answer: level.accept_any_answer ?? level.acceptAnyAnswer ?? false,
        user_answer: userAnswer,
        image: level.image,
        textContent: level.textContent,
        qrData: level.qrData,
        description: level.description
      };
    });
    this.levelsDrawerVisible = true;
  }

  // Close drawer
  closeLevelsDrawer(): void {
    this.levelsDrawerVisible = false;
    this.selectedParticipant = null;
    this.selectedParticipantLevels = [];
  }
}

